import { api } from "@/auth/api";
import { settings } from "@/settings";
import { filter, OrderStatusEnum } from "@/types";
import { globalConfig, headers,operator, setOtherFilters, SELECTED_USER_NETWORK } from "@/utils";
import { formatDateLocale, getContainsValue } from "@/utils/configuration/formatters-config";
import axios from "axios";
import { getFiltersStoreAdditionalInfo, getFiltersStoreAdditionalInfoBatch, getInOperator } from "../picklist";
import {getTaskCountByUser} from "@/store/services/task/workflow";
import moment from "moment";
import store from "@/store";

const countRequestMap = new Map<string, Promise<void>>();

export function clearFilterCountCache() {
  countRequestMap.clear();
}

function getFilterRequestKey(currentFilter: any): string | null {
  if (currentFilter?.resourceUid) {
    return `resource:${currentFilter.resourceUid}`;
  }
  if (currentFilter?.businessData) {
    try {
      return `payload:${JSON.stringify(currentFilter.businessData)}`;
    } catch (_err) {
      return null;
    }
  }
  return null;
}

async function fetchFilterOwner(filter: any): Promise<string | null> {
  if (!filter?.resourceUid) {
    return null;
  }
  try {
    const moreInfoFilter = {
      filterId: filter.resourceUid,
    };
    const moreInfo = await getFiltersStoreAdditionalInfo(moreInfoFilter);
    if (Array.isArray(moreInfo) && moreInfo.length > 0) {
      return moreInfo[0]._who_created || null;
    }
  } catch (error) {
    console.error("Failed to fetch filter metadata", error);
  }
  return null;
}

async function fetchFilterOwnersBatch(filters: any[]): Promise<Map<string, string>> {
    const ownerMap = new Map<string, string>();
    const filterIds = filters
        .filter((f: any) => f.resourceUid && !f.whoCreated)
        .map((f: any) => f.resourceUid);

    if (filterIds.length === 0) {
        return ownerMap;
    }

    try {
        const results = await getFiltersStoreAdditionalInfoBatch(filterIds);
        if (Array.isArray(results)) {
            results.forEach((row: any) => {
                if (row.filterid && row._who_created) {
                    ownerMap.set(row.filterid, row._who_created);
                }
            });
        }
    } catch (error) {
        console.error("Failed to fetch filter owners in batch", error);
    }
    return ownerMap;
}

function getRoleUser(){
  const connectedUser = store.getters["authModule/getUserConnected"];
  const userConnectedFromLocalStorage =  store.getters["authModule/getUserConnectedFromLocalStorage"] 
  const user = connectedUser?.userId ? connectedUser : userConnectedFromLocalStorage
  return {
    userUid: user?.userId,
    userName:user?.username,
    partyId: user?.partyPersonId?.resourceUid,
    isSecretary: user.authorities?.find((el: { authority: string }) => el.authority === "ROLE_SECRETARY" || el.authority === "ROLE_ASS_COMM" || el.authority ===  "ROLE_LIV"),
    isAssistant: user.authorities?.find((el: { authority: string }) => el.authority === "ROLE_ASSISTANT"),
    isAgent: user.authorities?.find((el: { authority: string }) => el.authority === "ROLE_AGENT"),
    isCDV: user.authorities?.find((el: { authority: string }) => el.authority === "ROLE_CDV"),
    isSales: user.authorities?.find((el: { authority: string }) => el.authority === "ROLE_SALES"),
    isPrepateur: user.authorities?.find((el: { authority: string }) => el.authority === "ROLE_PREPATEUR"),
    isMetteurEnMain: user.authorities?.find((el: { authority: string }) => el.authority === "ROLE_METTEUR"),
    isAuditor: user.authorities?.find((el: { authority: string }) => el.authority === "ROLE_AUDITORS"),
    isDirector: user.authorities?.find((el: { authority: string }) => el.authority === "ROLE_DIRECTOR")
  };
}

export async function getSavedFilters(queryId: any) {

    const listNotFiltered = await listOfFilters(queryId)
    listNotFiltered.sort((a: filter, b: filter) => a.numOrder - b.numOrder);

    const selectedUserNetwork : any = store.state.authModule?.selectedUserNetwork 
            && store.state.authModule?.selectedUserNetwork.value
            ? store.state.authModule?.selectedUserNetwork.value
            : ""
    const selectedUserNetworkFromLocalStorage = store.getters["authModule/getSelectedUserNetworkFromLocalStorage"]
    const userNetwork = selectedUserNetwork ? selectedUserNetwork : selectedUserNetworkFromLocalStorage

    const roleUser = getRoleUser();

    // Prepare all filters first
    for (const filter of listNotFiltered) {
        if (filter.businessData && filter.businessData.queryId) {
          if (filter?.businessData?.otherFilters?.expressions?.[0]?.and?.operators) {
            filter.businessData.otherFilters.expressions[0].and.operators =
            filter.businessData.otherFilters.expressions[0].and.operators.filter(
                (operator:any) => !isOrderVisibilityOperator(operator)
            );
          }
          if(!roleUser.isCDV && !roleUser.isDirector && !roleUser.isSecretary && !roleUser.isAuditor && !(filter && filter.notificationFilter) && !filter.isRoleAdmin){
            filter?.businessData?.otherFilters?.expressions[0]?.and?.operators.push(
              buildOrderVisibilityOperator(roleUser, filter.businessData.queryId)
            );
          }
        } else if(filter.screenId === 'search-task'){
          const searchVariables = JSON.parse(filter.businessData.searchVariables);
            if(searchVariables.length){
              for (const variable of searchVariables) {
                if (variable.name === 'orderNetworkNode' && variable.value !==userNetwork){
                  variable.value = userNetwork
                }
              }
              filter.businessData.searchVariables = JSON.stringify(searchVariables)
            }
        }
    }

    const connectedUser = store.getters["authModule/getUserConnected"];
    const userConnectedFromLocalStorage =  store.getters["authModule/getUserConnectedFromLocalStorage"] 
    const userName = connectedUser?.username ? connectedUser?.username : userConnectedFromLocalStorage?.username  

    const existingFilters = Array.isArray(store.state.filterModule?.listOfFilters)
      ? store.state.filterModule.listOfFilters
      : [];
    const existingFiltersMap = new Map(
      existingFilters
        .filter((item: any) => item?.resourceUid)
        .map((item: any) => [item.resourceUid, item])
    );

    const ownerMap = await fetchFilterOwnersBatch(listNotFiltered);

    listNotFiltered.forEach((filter: any) => {
        if (!filter.whoCreated && filter.resourceUid && ownerMap.has(filter.resourceUid)) {
            filter.whoCreated = ownerMap.get(filter.resourceUid)!;
        }
    });
    const accessibleFilters: any[] = [];
    listNotFiltered.forEach((filter: any) => {
        const preparedFilter = {
            ...filter,
            count: filter.count ?? 0,
            countLoading: true,
            whoCreated: filter.whoCreated ?? null,
        };

        const isPrivate = filter.hasPublicAccess === false;
        const isOwner = preparedFilter.whoCreated === userName;

        const existing = preparedFilter.resourceUid
          ? existingFiltersMap.get(preparedFilter.resourceUid)
          : undefined;

        const filterWithState = existing
          ? {
              ...preparedFilter,
              count: existing.count ?? preparedFilter.count,
              countLoading:
                existing.countLoading !== undefined
                  ? existing.countLoading
                  : preparedFilter.countLoading,
            }
          : preparedFilter;

        if (existing) {
            if (isPrivate && !isOwner && filterWithState.resourceUid) {
                store.commit("filterModule/removeFilter", filterWithState.resourceUid);
                return;
            }
            accessibleFilters.push(filterWithState);
            return;
        }

        if (!isPrivate || isOwner) {
            accessibleFilters.push(filterWithState);
        }
    });

    const updateCount = async (currentFilter: any) => {
      try {
        if (currentFilter.businessData && currentFilter.businessData.queryId) {
          const data: any = await searchCount(currentFilter)
          currentFilter.count = data.count
        } else if(currentFilter.screenId === 'search-task'){
            const filterCamunda: any = {...currentFilter}

            delete filterCamunda.screenId
            delete filterCamunda.description
            delete filterCamunda.color
            delete filterCamunda.hasPublicAccess
            delete filterCamunda.numOrder
            delete filterCamunda.whoCreated
            delete filterCamunda.count
            delete filterCamunda.countLoading
            delete filterCamunda.groupFilter

            filterCamunda.initializeVariables = currentFilter.businessData.initializeVariables
            filterCamunda.candidateGroups = currentFilter.businessData.candidateGroups
            filterCamunda.searchVariables = currentFilter.businessData.searchVariables
            filterCamunda.taskDefinitionKeyIn = currentFilter.businessData.taskDefinitionKeyIn
            filterCamunda.includeAssignedTasks = currentFilter.businessData.includeAssignedTasks
            currentFilter.businessData && currentFilter.businessData.assigneeIn
            ? filterCamunda.assigneeIn = currentFilter.businessData.assigneeIn
            : null
            currentFilter.businessData && currentFilter.businessData.assignee
            ? filterCamunda.assignee = currentFilter.businessData.assignee
            : null

            const data: any = await getTaskCountByUser(filterCamunda)
            currentFilter.count = data.data.count
        } else {
          currentFilter.count = 0
        }
      } catch (error) {
        console.error("Failed to update filter count", error);
      } finally {
        currentFilter.countLoading = false;
      }
    };

    const scheduleCountUpdate = (currentFilter: any) => {
      const requestKey = getFilterRequestKey(currentFilter);
      if (!requestKey) {
        return;
      }
      if (countRequestMap.has(requestKey)) {
        return;
      }

      const request = updateCount(currentFilter)
        .finally(() => {
          if (currentFilter.resourceUid) {
            store.commit("filterModule/upsertFilter", {
              resourceUid: currentFilter.resourceUid,
              count: currentFilter.count,
              countLoading: currentFilter.countLoading,
            });
          }
          countRequestMap.delete(requestKey);
        });

      countRequestMap.set(requestKey, request);
    };

    accessibleFilters.forEach((filter: any) => {
      scheduleCountUpdate(filter);
    });

    return accessibleFilters
}

async function listOfFilters(queryId: any) {
  const systemUid = "odm-configuration";
  return (
    await api().get(
      `/${systemUid}/api/1/${systemUid}/filterstore/screenid/${queryId}/`
    )
  ).data;
}

export const saveFilter = async (payload: any) => {
  const systemUid = "odm-configuration";
  delete payload.count;
  return (
    await api().post(`/${systemUid}/api/1/${systemUid}/filterstore/`, payload)
  ).data;
};


export const updateFilter = async (payload: any) => {
  const systemUid = 'odm-configuration'
  delete payload.count
  delete payload.whoCreated
  delete payload.countLoading
  return (await api().put(`/${systemUid}/api/1/${systemUid}/filterstore/` + payload.resourceUid + '/', payload)).data
}

/** party_party columns making up the searchable identity of a third party. */
const CLIENT_NAME_COLUMNS = ["first_name", "family_name", "commercial_name", "_name"];

/** Minimum length of a token kept for the name search. */
const CLIENT_NAME_MIN_TOKEN_LENGTH = 2;

/**
 * Builds the (table, field) pair expected by the dataservice engine to target the
 * concatenation of a third party's identity columns.
 *
 * The engine assembles the predicate as `table + "." + lower(field)`
 * (QueryParser.buildExpression), which allows injecting a SQL expression by
 * splitting it around its last dot. The expression relies on `||` and `coalesce`
 * (IMMUTABLE) rather than `concat_ws` (STABLE) so that it stays indexable by a
 * GIN pg_trgm index built on the very same expression.
 */
function getClientNameExpression(alias: string): { table: string; field: string } {
  const lastColumn = CLIENT_NAME_COLUMNS[CLIENT_NAME_COLUMNS.length - 1];
  const parts = CLIENT_NAME_COLUMNS.slice(0, -1).map(column => `coalesce(${alias}.${column},'')`);
  parts.push(`coalesce(${alias}`);
  return {
    table: `lower(${parts.join(" || ' ' || ")}`,
    field: `${lastColumn},''))`
  };
}

/**
 * Client name search filter, tolerant to both "family name first name" and
 * "first name family name" input.
 *
 * The input is split into tokens; every token must be contained in the
 * concatenation of first name + family name + commercial name + third party
 * label (AND between tokens), which makes input order and composed names
 * irrelevant.
 *
 * @param rawName raw user input
 * @param alias   alias of the party_party table in the targeted query
 * @returns an operator group to push at the end of the `operators` array, or
 *          null when the input holds no usable token
 */
export function buildClientNameFilter(rawName: string, alias: string): any | null {
  if (!rawName) {
    return null;
  }
  const tokens = Array.from(new Set(
    rawName.trim().toLowerCase().split(/[\s,;]+/).filter(token => token.length >= CLIENT_NAME_MIN_TOKEN_LENGTH)
  ));
  if (tokens.length === 0) {
    return null;
  }
  const { table, field } = getClientNameExpression(alias);
  return {
    and: {
      operators: tokens.map(token =>
        getOperator(table, field, "LIKE", getContainsValue(token), "clientName")
      )
    }
  };
}

/**
 * Splits a multi-value filter input (values joined by ";", as emitted by
 * MultiValueInput) into trimmed, non-empty, case-insensitively unique values.
 */
export function splitMultiValues(rawValue: any, separator: RegExp = /;/): string[] {
  if (!rawValue) {
    return [];
  }
  const values = Array.isArray(rawValue) ? rawValue : String(rawValue).split(separator);
  const seen = new Set<string>();
  return values
    .map((value: any) => String(value).trim())
    .filter((value: string) => {
      const key = value.toLowerCase();
      if (!value || seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
}

/**
 * "Contains" search on a column accepting several values (OR between values).
 *
 * A single value keeps producing a plain expression, which can be pushed
 * anywhere in the operators array. Several values produce an OR group, which
 * must be pushed at the end of the array (see QueryParser.buildFilterClause):
 * callers therefore receive it through `groups`.
 */
export function pushMultiValueLikeFilter(
  operators: any[],
  groups: any[],
  rawValue: any,
  table: string,
  field: string,
  parameter: string
) {
  const values = splitMultiValues(rawValue);
  const buildLike = (value: string) =>
    getOperator(
      table,
      field,
      "LIKE",
      globalConfig.formatters.getEqualValue("%" + value + "%").toLowerCase(),
      parameter
    );
  if (values.length === 1) {
    operators.push(buildLike(values[0]));
  } else if (values.length > 1) {
    groups.push(buildGroupedConditions(values.map(value => [buildLike(value)])));
  }
}

/**
 * Client name filter accepting several names joined by ";" (OR between names,
 * AND between the tokens of each name, see buildClientNameFilter).
 *
 * @param aliases one party alias, or several (e.g. client and end client, EFLO-793):
 *                a name matches when it is found on any of them
 */
export function buildMultiClientNameFilter(rawNames: any, aliases: string | string[]): any | null {
  const aliasList = Array.isArray(aliases) ? aliases : [aliases];
  const nameFilters = splitMultiValues(rawNames)
    .map(name => {
      const aliasFilters = aliasList.map(alias => buildClientNameFilter(name, alias)).filter(Boolean);
      return aliasFilters.length > 1 ? { or: { operators: aliasFilters } } : aliasFilters[0] ?? null;
    })
    .filter(nameFilter => nameFilter !== null);
  if (nameFilters.length === 0) {
    return null;
  }
  if (nameFilters.length === 1) {
    return nameFilters[0];
  }
  return { or: { operators: nameFilters } };
}

export function buildGroupedConditions(filters: any[][]): any {
  return {
    or: {
      operators: filters.map(item => ({
        and: {
          operators: item
        }
      }))
    }
  };
}
const SELLER_PARTY_QUERY_IDS = ["order-search-filter-v2", "order-search-filter-backup"];
export function buildOrderVisibilityOperator(roleUser: { userName?: string; partyId?: string; isSales?: unknown }, queryId?: string): any {
  if (roleUser.isSales && roleUser.partyId && queryId && SELLER_PARTY_QUERY_IDS.includes(queryId)) {
    return getOperator(
      "order_seller_party",
      "party_id",
      "EQUAL",
      globalConfig.formatters.getEqualValue(roleUser.partyId),
      "party_id"
    );
  }
  return getOperator(
    "lower(order_order",
    "_who_created)",
    "EQUAL",
    globalConfig.formatters.getEqualValue(roleUser.userName).toLowerCase(),
    "_who_created"
  );
}
export function isOrderVisibilityOperator(operator: any): boolean {
  const { table, field } = operator?.expression || {};
  return (table === "lower(order_order" && field === "_who_created)")
    || (table === "order_seller_party" && field === "party_id");
}
export function getOperator(
  table: string,
  field: string,
  operator: string,
  value?: any,
  parameter?: string,
  inParameters?: string[]
) {
  return {
    expression: {
      table: table,
      field: field,
      operator: operator,
      value: value,
      parameter: parameter,
      inValue:
        inParameters && inParameters.length > 0
          ? {
              values: inParameters,
            }
          : null,
    },
  };
}

export function getQcUser(filter: any , offset?: any, limit?: any) {

  const operators = [];

  const qc_header: any = {
    qc: {
      queryId: "user-lease-forge-user-with-usergroup",
      offset: offset || 0,
      limit: limit || 100,
      parameters: {
        ...filter,
      },
    },
  };

  const LEASE_FORGE_USER = "user_lease_forge_user";
  const USER_GROUP = "userGroups";
  const PARTY_PARTY_ROLE_NETWORK = "userRoles"

  if (filter?.username) {
    operators.push(
      operator({
        val: filter.username,
        table: LEASE_FORGE_USER,
        dbField: "username",
      }).LIKE
    );
  }

  if (filter?.firstName) {
    operators.push(
      operator({
        val: filter.firstName,
        table: LEASE_FORGE_USER,
        dbField: "first_name",
      }).LIKE
    );
  }

  if (filter?.lastName) {
    operators.push(
      operator({
        val: filter.lastName,
        table: LEASE_FORGE_USER,
        dbField: "last_name",
      }).LIKE
    );
  }


  if (filter?.email) {
    operators.push(
      operator({
        val: filter.email,
        table: LEASE_FORGE_USER,
        dbField: "email",
      }).LIKE
    );
  }

  if (filter?.status) {
    operators.push(
      operator({
        val: filter.status,
        table: LEASE_FORGE_USER,
        dbField: "enabled",
      }).EQUAL
    );
  }

  if (filter?.notExpired) {
    operators.push(
      operator({
        val: filter.notExpired,
        table: LEASE_FORGE_USER,
        dbField: "account_not_expired",
      }).EQUAL
    );
  }

  if (filter?.notLocked) {
    operators.push(
      operator({
        val: filter.notLocked,
        table: LEASE_FORGE_USER,
        dbField: "account_not_locked",
      }).EQUAL
    );
  }

  if (filter?.credintialsNotExpired) {
    operators.push(
      operator({
        val: filter.credintialsNotExpired,
        table: LEASE_FORGE_USER,
        dbField: "credentials_not_expired",
      }).EQUAL
    );
  }
  
  if (filter?.userGroups) {
    operators.push(
      getOperator(
        "cast(userGroups",
        "group_code  AS text)",
        "LIKE",
        globalConfig.formatters.getLikeValue(filter.userGroups),
        "group_code"
    )
  );
  }
  if (filter?.isClientPortalGroup === true) {
      operators.push(
        getOperator(
          "cast(userGroups",
          "group_code  AS text)",
            "LIKE",
            globalConfig.formatters.getEqualValue('%Grp_Portail_Client%'),
            "group_code"
        )
      );
  }

  if (filter?.networkNodes) {
    operators.push(
      getOperator(
        "cast(userRoles",
        "network_node_id  AS text)",
        "LIKE",
        globalConfig.formatters.getLikeValue(filter.networkNodes),
        "network_node_id"
        )
      );
  }

  setOtherFilters(operators, qc_header);

  if (operators.length > 0) {
    qc_header.qc.otherFilters = {
      expressions: [
        {
          and: {
            operators
          }
        }
      ]
    }
  }
  return qc_header.qc;

}


export function getQcGaranty(filter : any , offset? : any , limit? : any) {
  const operators = [];

  const qc_header: any = {
    qc: {
      queryId: "product-product-feature",
      offset: offset || 0,
      limit: limit || 100,
      parameters: {
        ...filter,
      },
    },
  };

  const PRODUCT_FEATURE = "product_product_feature";

  if (filter?.reference) {
    operators.push(
      operator({
        val: filter.reference,
        table: PRODUCT_FEATURE,
        dbField: "_reference",
      }).LIKE
    );
  }


  if (filter?.type) {

    operators.push(
      operator({
        val: filter.type,
        table: PRODUCT_FEATURE,
        dbField: "_discriminator",
      }).LIKE
    );
  }

  if (filter?.guaranty_name) {
    operators.push(
      operator({
        val: filter.guaranty_name,
        table: PRODUCT_FEATURE,
        dbField: "_name",
      }).LIKE
    );
  }

  if (filter?.status) {
    operators.push(
      operator({
        val: filter.status,
        table: PRODUCT_FEATURE,
        dbField: "status_code",
      }).LIKE
    );
  }

  setOtherFilters(operators, qc_header);

  if (operators.length > 0) {
    qc_header.qc.otherFilters = {
      expressions: [
        {
          and: {
            operators
          }
        }
      ]
    }
  }
  return qc_header.qc;

}

export function getQcGood(filter: any , offset?: any , limit?: any) {
    const qc_header: any = {
      qc: {
        queryId: 'asset-search-filter',
        offset: offset || 0,
        limit: limit || 100
      }
    }

    qc_header.qc.parameters = {
      ...filter
    }
    const operators = []


    if (filter.reference) {
      operators.push(getOperator("lower(product_product_feature", "_reference)", "LIKE", globalConfig.formatters.getEqualValue(filter.reference + '%').toLowerCase(), "_reference"))
    }
    if (filter.name) {
      operators.push(getOperator("lower(product_product_feature", "_name)", "LIKE", globalConfig.formatters.getEqualValue(filter.name + '%').toLowerCase(), "name"))
    }
    if (filter.brand) {
      operators.push(getOperator("lower(ppc_brand", "category_class_code)", "LIKE", globalConfig.formatters.getEqualValue('%' + filter.brand.label + '%').toLowerCase(), "id"))
    }
    if (filter.model) {
      operators.push(getOperator("lower(ppc_model", "category_class_code)", "LIKE", globalConfig.formatters.getEqualValue('%' + filter.model.label + '%').toLowerCase(), "id"))
    }
    if (filter.status) {
      operators.push(getOperator("product_product_feature", "status_code", "EQUAL", globalConfig.formatters.getEqualValue(filter.status), "status"))
    }
    if (filter.family) {
      operators.push(getOperator("lower(ppc_famille", "category_class_code)", "LIKE", globalConfig.formatters.getEqualValue('%' + filter.family.id + '%').toLowerCase(), "id"))
    }
    if (filter.catalog) {
      operators.push(getOperator("lower(product_product_catalog", "_reference)", "LIKE", globalConfig.formatters.getEqualValue('%' + filter.catalog), "_reference"))
    }
    if (filter.supplier && filter.supplier.id) {
      operators.push(getOperator("product_product_catalog", "provider_resourceuid", "LIKE", globalConfig.formatters.getEqualValue(filter.supplier.id.resourceUid), "provider_resourceuid"))
    }
    if (filter.inventory) {
      operators.push(getOperator("product_inventory_item", "asset_product_id", "ISNOTNULL"));
      }
      if (filter.type) {
        operators.push(
          getOperator(
            "lower(product_product_classification",
            "category_class_code)",
            "LIKE",
            globalConfig.formatters.getEqualValue("%" + filter.type+ "%").toLowerCase(),
            "category_class_code"
          )
        );
      }


    if (operators.length > 0) {
      qc_header.qc.otherFilters = {
        expressions: [
          {
            and: {
              operators
            }
          }
        ]
      }
    }
    return qc_header.qc;

}

export function getQcScale(filter: any, offset?: any , limit?: any) {
  const operators = [];

    const qc_header: any = {
        qc: {
            queryId: 'scales-search-filter',
            offset: offset || 0,
            limit: limit || 100,
            parameters: {
                ...filter
            }
        }
    };

    const PRODUCT_FEATURE = "product_product_feature";
    const PRODUCT_CLASSIFICATION = "product_product_classification";

    if (filter?.name) {
        operators.push(
            operator(
                { val: filter.name, table: PRODUCT_FEATURE, dbField: "_name" }
            ).LIKE
        );
    }

    if (filter?.reference) {
        operators.push(
            operator(
                { val: filter.reference, table: PRODUCT_FEATURE, dbField: "_reference" }
            ).LIKE
        );
    }

    if (filter?.validity?.from && filter?.validity?.from !== '') {
        operators.push(
            operator(
                { val: filter.validity.from, table: PRODUCT_FEATURE, dbField: "validity_from" }
            ).GREATER_THAN_EQUAL
        );
    }

    if (filter?.validity?.until && filter?.validity?.until !== '') {
        operators.push(
            operator(
                { val: filter.validity.until, table: PRODUCT_FEATURE, dbField: "validity_until" }
            ).LESS_THAN_EQUAL
        );
    }

    if (filter?.financialProfile) {
        operators.push(
            operator(
                { val: filter.financialProfile, table: PRODUCT_FEATURE, dbField: "financial_profile_code" }
            ).EQUAL
        );
    }

    if (filter?.status) {
        operators.push(
            operator(
                { val: filter.status, table: PRODUCT_FEATURE, dbField: "status_code" }
            ).EQUAL);
    }

    /**
     * Checks if the array contains any value that is not undefined.
     *
     * @template T
     * @param {T[]} arr - The array to check.
     * @return {boolean} True if the array contains any non-undefined value, false otherwise.
     */
    const notOnlyUndefined = <T>(arr: T[]): boolean => arr.some(item => item !== undefined);

    /**
     * Removes any undefined values from an array.
     *
     * @template T
     * @param {T[]} arr - The array to remove undefined values from.
     * @return {T[]} The array without any undefined values.
     */
    const removeTheUndefined = <T>(arr: T[]): T[] => arr.filter(item => item !== undefined);

    if (filter?.category && filter.category.length > 0 && notOnlyUndefined(filter.category)) {
        operators.push(
            operator(
                {
                    val: removeTheUndefined(filter.category),
                    table: PRODUCT_CLASSIFICATION,
                    dbField: "category_code"
                }
            ).IN
        );
    }

    if (filter?.class && filter.class.length > 0 && notOnlyUndefined(filter.class)) {
        operators.push(
            operator(
                {
                    val: removeTheUndefined(filter.class),
                    table: PRODUCT_CLASSIFICATION,
                    dbField: "category_class_code"
                }
            ).IN
        );
    }

    setOtherFilters(operators, qc_header);

    if (operators.length > 0) {
      qc_header.qc.otherFilters = {
        expressions: [
          {
            and: {
              operators,
            },
          },
        ],
      };
    }
    return qc_header.qc;
}

export function getQcAvailability(filter: any, offset?: any, limit?: any) {
  const qc_header: any = {
    qc: {
      queryId: 'product-availability-filter-search',
      offset: offset || 0,
      limit: limit || 100,
      parameters: {
        ...filter
      }
    }
  };

  const operators = [];
  const PRODUCT_AVAILABILITY = "product_product_availability";

  if (filter?.name) {
    operators.push(
      operator(
        { val: filter.name, table: PRODUCT_AVAILABILITY, dbField: "application_name" }
      ).LIKE
    );
  }

  if (filter?.validity?.from && filter?.validity?.from !== '') {
    operators.push(
      operator(
        { val: filter.validity.from, table: PRODUCT_AVAILABILITY, dbField: "validity_from" }
      ).GREATER_THAN_EQUAL
    );
  }

  if (filter?.validity?.until && filter?.validity?.until !== '') {
    operators.push(
      operator(
        { val: filter.validity.until, table: PRODUCT_AVAILABILITY, dbField: "validity_until" }
      ).LESS_THAN_EQUAL
    );
  }

  if (filter?.context) {
    operators.push(
      operator(
        { val: filter.context, table: PRODUCT_AVAILABILITY, dbField: "_context" }
      ).LIKE
    );
  }

  if (!filter?.context) {
           operators.push(getOperator(PRODUCT_AVAILABILITY, "_context", "EQUAL", "'DISTRIBUTION'", "_context"))
       }

  setOtherFilters(operators, qc_header);
  
  if (operators.length > 0) {
    qc_header.qc.otherFilters = {
      expressions: [
        {
          and: {
            operators,
          },
        },
      ],
    };
  }
  return qc_header.qc;

}

export function getQcOrder(filter: any,offset?: any, limit?: any) {
  const qc_header: Record<string, any> = {
    qc: {
        queryId: "order-search-filter-v2",
        offset: offset || 0,
        limit: limit || 100,
    },
  }

  // qc_header.qc.parameters = {
  //   ...filter,
  // };
  const operators = [];
  let clientNameFilter: any = null;
  const operatorsNotificationFilter= [];
  const operatorsChassisRererence= [];
  const multiValueGroups: any[] = [];
  let rdvOutsideWindowFilter: any = null; // EFLO-1004

   const roleUser =getRoleUser()
   if(!roleUser.isCDV && !roleUser.isDirector && !roleUser.isSecretary && !roleUser.isAuditor && !(filter && filter.notificationFilter) && !filter.isRoleAdmin){
    operators.push(buildOrderVisibilityOperator(roleUser, qc_header.qc.queryId));
   }
   if (filter && filter.userNetwork && !filter.from){
      operators.push(
        getOperator(
          "party_network_node",
          "id",
          "EQUAL",
          globalConfig.formatters.getEqualValue(filter.userNetwork.value),
          "party_network_node"
        )
      );
   }

   if (filter.otsSeverityList&& filter.otsSeverityList.length > 0) {
    operators.push( 
      getInOperator(
        "order_order_ots_history",
        "max_severity_code",
        "IN",
        null,
        null,
        globalConfig.formatters.getInValue(filter.otsSeverityList)
      )
    );
  }  
  if(filter.planingEvent){
    operators.push(getOperator("order_order_item", "external_reference", "ISNOTNULL"));
    operators.push(getOperator("order_order", "status_code", "NOT_EQUAL", "'CANCELLED'", "status_code"))
    if(filter.sellername){
      operators.push(getOperator("order_order","assigned_sales_person","EQUAL",globalConfig.formatters.getEqualValue(filter.sellername),"assigned_sales_person")
      );
    }
  }

  if (filter.date_RDVToggle === true  || filter.date_RDVToggle === false) {
      const today = new Date(); // J0
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0'); 
      const day = String(today.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      const today_1 = new Date();
      today_1.setDate(today_1.getDate() + 3); // J+3
      const year_1 = today_1.getFullYear();
      const month_1 = String(today_1.getMonth() + 1).padStart(2, '0'); 
      const day_1 = String(today_1.getDate()).padStart(2, '0');
      const formattedDate_1 = `${year_1}-${month_1}-${day_1}`;
      if (filter.date_RDVToggle) {
          operators.push(
                getOperator(
                  "order_order_item",
                  "customer_appointment_date",
                  "GREATER_THAN_EQUAL", // inclure aujourd'hui
                  globalConfig.formatters.getEqualValue(formattedDate),
                  "deliveryDateTo"
                )
              );
              operators.push(
                getOperator(
                  "order_order_item",
                  "customer_appointment_date",
                  "LESS_THAN_EQUAL",
                  globalConfig.formatters.getEqualValue(formattedDate_1),
                  "deliveryDateTo"
                )
              );
       } else {
            rdvOutsideWindowFilter = {
              or: {
                operators: [
                  getOperator("order_order_item", "customer_appointment_date", "ISNULL"),
                  getOperator(
                    "order_order_item",
                    "customer_appointment_date", 
                    "LESS_THAN",globalConfig.formatters.getEqualValue(formattedDate), 
                    "rdvClientFrom"),
                  getOperator("order_order_item", 
                    "customer_appointment_date", 
                    "GREATER_THAN",
                    globalConfig.formatters.getEqualValue(formattedDate_1), 
                    "rdvClientTo"),
                ]
              }
            };
       }
  }


  if (filter.date_MADAToggle) {
      const today = new Date();
      today.setDate(today.getDate() - 3);
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0'); 
      const day = String(today.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      const today_1 = new Date();
      const year_1 = today_1.getFullYear();
      const month_1 = String(today_1.getMonth() + 1).padStart(2, '0'); 
      const day_1 = String(today_1.getDate()).padStart(2, '0');
      const formattedDate_1 = `${year_1}-${month_1}-${day_1}`;
      operators.push(
            getOperator(
              "order_order_asset",
              "effective_delivery_factory_date",
              "GREATER_THAN",
              globalConfig.formatters.getEqualValue(formattedDate),
              "deliveryDateTo"
            )
          );
          operators.push(
            getOperator(
              "order_order_asset",
              "effective_delivery_factory_date",
              "LESS_THAN_EQUAL",
              globalConfig.formatters.getEqualValue(formattedDate_1),
              "deliveryDateTo"
            )
          );
  }
  if (filter.date_MADCToggle) {
    const today = new Date();
      today.setDate(today.getDate() - 7);
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0'); 
      const day = String(today.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      const today_1 = new Date();
      const year_1 = today_1.getFullYear();
      const month_1 = String(today_1.getMonth() + 1).padStart(2, '0'); 
      const day_1 = String(today_1.getDate()).padStart(2, '0');
      const formattedDate_1 = `${year_1}-${month_1}-${day_1}`;
      operators.push(
            getOperator(
              "order_order_asset",
              "availability_car_date",
              "GREATER_THAN_EQUAL",
              globalConfig.formatters.getEqualValue(formattedDate),
              "deliveryDateTo"
            )
          );
      operators.push(
            getOperator(
              "order_order_asset",
              "availability_car_date",
              "LESS_THAN_EQUAL",
              globalConfig.formatters.getEqualValue(formattedDate_1),
              "deliveryDateTo"
            )
          );
  }
  if (filter.documentStatus && Array.isArray(filter.documentStatus) && filter.documentStatus.length > 0) {
       const isPending = filter.documentStatus.includes('PENDING');
       if (isPending) {
          // Document pas encore reçu : aucune ligne dans "supportingdocument_received_file"
          operators.push(
            getOperator("received", "status_code", "ISNULL")
          );
       } else {
          // Document reçu avec un statut donné
          operators.push(
            getInOperator(
              "received",
              "status_code",
              "IN",
              null,
              null,
              globalConfig.formatters.getInValue(filter.documentStatus)
            )
          );
       }
  }
  if(filter.documentType){
    operators.push(
            getOperator(
              "received",
              "document_type_code",
              "EQUAL",
              globalConfig.formatters.getEqualValue(filter.documentType),
              "document_type_code"
            )
          );
  }

  if (filter.filterReference) {
    const splitedOrders = filter.filterReference
      .split(/[;,]/)
      .map((item: any) => item.trim())
      .filter((item: string) => item !== "");

    if (splitedOrders.length > 1) {
      operators.push(
        getInOperator(
          "order_order",
          "_reference",
          "IN",
          null,
          null,
          globalConfig.formatters.getInValue(splitedOrders)
        )
      );
    } else if (splitedOrders.length === 1) {
      operators.push(
        getOperator(
          "lower(order_order",
          "_reference)",
          "LIKE",
          globalConfig.formatters
            .getEqualValue("%" + splitedOrders[0] + "%")
            .toLowerCase(),
          "_reference"
        )
      );
    }
  }
  if (filter.externalReference) {
    pushMultiValueLikeFilter(operators, multiValueGroups, filter.externalReference, "lower(order_order_item", "external_reference)", "external_reference");
  }
  if (filter.filterStatus) {
    const filterStatusList = Array.isArray(filter.filterStatus)? filter.filterStatus: [filter.filterStatus];
    if (filterStatusList.length > 0) {
      operators.push(
        getInOperator(
          "order_order",
          "status_code",
          "IN",
          null,
          null,
          globalConfig.formatters.getInValue(filterStatusList)
        )
      );
    }
  }
  if (filter.orderPhases) {
     if(!filter.statusPhases)
     {
        operators.push(
          getOperator(
            "order_order_status",
            "internal_code",
            "EQUAL",
            globalConfig.formatters.getEqualValue(filter.orderPhases),
            "internal_code"
          )
        );
     }
     else {
      switch (filter.orderPhases) {
            /* Phase 1 - ORDER */  /*VN +VO*/
            case OrderStatusEnum.ORDER:
              if (filter.statusPhases === "TODO") {
                operators.push(
                  getOperator(
                    "order_order_status",
                    "internal_code",
                    "EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.ORDER),
                    "internal_code"
                  )
                );
                operators.push(
                  getOperator(
                    "order_order",
                    "status_code",
                    "EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.ORDER),
                    "status_code"
                  )
                );
              }
              if (filter.statusPhases === "IN_PROGRESS") {
                operators.push(
                  getOperator(
                    "order_order_status",
                    "internal_code",
                    "EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.ORDER),
                    "internal_code"
                  )
                );
                operators.push(
                  getOperator(
                    "order_order",
                    "status_code",
                    "NOT_EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.ORDER),
                    "status_code"
                  )
                );
              }
              if (filter.statusPhases === "DONE") {
                 operators.push(
                  getOperator(
                    "order_order_status",
                    "internal_code",
                    "EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.VALIDATION),
                    "internal_code"
                  )
                );
              }
              break;
            
            /* Phase 2-VALIDATION*/ 
            case OrderStatusEnum.VALIDATION:
              if (filter.statusPhases === "TODO") { /*VN +VO*/
                operators.push(
                  getOperator(
                    "order_order_status",
                    "internal_code",
                    "EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.VALIDATION),
                    "internal_code"
                  )
                );
                operators.push(
                  getOperator(
                    "order_order",
                    "status_code",
                    "EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.ORDER_PREREQ_DONE),
                    "status_code"
                  )
                );
              }
              if (filter.statusPhases === "IN_PROGRESS") { /*VN +VO*/
                operators.push(
                  getOperator(
                    "order_order_status",
                    "internal_code",
                    "EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.VALIDATION),
                    "internal_code"
                  )
                );
                operators.push(
                  getOperator(
                    "order_order",
                    "status_code",
                    "NOT_EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.ORDER_PREREQ_DONE),
                    "status_code"
                  )
                );
              }
              if (filter.statusPhases === "DONE") {   /*VN +VO*/
                // Ajout d’un filtre combiné dans la liste des opérateurs
                operators.push(

                  // Méthode générique qui construit une condition de type :
                  // (condition1 AND condition2) OR (condition3 AND ...)
                  buildGroupedConditions([
                    [  //*VO*/
                      // Condition 1 : statut = PREPARATION
                      getOperator(
                        "order_order_status",
                        "internal_code",
                        "EQUAL",
                        globalConfig.formatters.getEqualValue(OrderStatusEnum.PREPARATION),
                        "internal_code"
                      ),

                      // Condition 2 : VO (flag_new = false)
                      getOperator(
                        "order_order_asset",
                        "flag_new",
                        "EQUAL",
                        globalConfig.formatters.getEqualValue("false"),
                        "flag_new"
                      )

                      // Ce groupe de conditions correspond :
                      // (status = PREPARATION AND flag_new = false)
                    ],

                    [ //*VN*/
                      // Condition 3 : statut = MANUFACTURING
                      getOperator(
                        "order_order_status",
                        "internal_code",
                        "EQUAL",
                        globalConfig.formatters.getEqualValue(OrderStatusEnum.MANUFACTURING),
                        "internal_code"
                      ),

                      // Ce groupe de conditions correspond à :
                      // (status = MANUFACTURING)
                    ]

                  ])
                );
              }
              break;
            
              /* Phase 3-MANUFACTURING*/  /*VN Seulement*/
            case OrderStatusEnum.MANUFACTURING:
              if (filter.statusPhases === "TODO") {
                operators.push(
                  getOperator(
                    "order_order_status",
                    "internal_code",
                    "EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.MANUFACTURING),
                    "internal_code"
                  )
                );
                operators.push(
                  getOperator(
                    "order_order",
                    "status_code",
                    "EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.ORDER_VALIDATED),
                    "status_code"
                  )
                );
              }
              if (filter.statusPhases === "IN_PROGRESS") {
                operators.push(
                  getOperator(
                    "order_order_status",
                    "internal_code",
                    "EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.MANUFACTURING),
                    "internal_code"
                  )
                );
                operators.push(
                  getOperator(
                    "order_order",
                    "status_code",
                    "NOT_EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.ORDER_VALIDATED),
                    "status_code"
                  )
                );
              }
              if (filter.statusPhases === "DONE") {
                 operators.push(getOperator("order_order_asset","flag_new","EQUAL",globalConfig.formatters.getEqualValue("true"),"flag_new"));
                 operators.push(
                  getOperator(
                    "order_order_status",
                    "internal_code",
                    "EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.PREPARATION),
                    "internal_code"
                  )
                );
              }
              break;
            
              /* Phase 4-PREPARATION*/ /*VN +VO*/
            case OrderStatusEnum.PREPARATION:
              if (filter.statusPhases === "TODO") {
                operators.push(
                  getOperator(
                    "order_order_status",
                    "internal_code",
                    "EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.PREPARATION),
                    "internal_code"
                  )
                );
                operators.push(
                  getOperator(
                    "order_order",
                    "status_code",
                    "EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.ORDER_MANUFACTURED),
                    "status_code"
                  )
                );
              }
              if (filter.statusPhases === "IN_PROGRESS") {
                operators.push(
                  getOperator(
                    "order_order_status",
                    "internal_code",
                    "EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.PREPARATION),
                    "internal_code"
                  )
                );
                operators.push(
                  getOperator(
                    "order_order",
                    "status_code",
                    "NOT_EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.ORDER_MANUFACTURED),
                    "status_code"
                  )
                );
              }
              if (filter.statusPhases === "DONE") {
                 operators.push(
                  getOperator(
                    "order_order_status",
                    "internal_code",
                    "EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.DELEVERY),
                    "internal_code"
                  )
                );
              }
              break;
            
              /* Phase 5-DELEVERY*/ /*VN +VO*/
            case OrderStatusEnum.DELIVERY:
              if (filter.statusPhases === "TODO") {
                operators.push(
                  getOperator(
                    "order_order_status",
                    "internal_code",
                    "EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.DELEVERY),
                    "internal_code"
                  )
                );
                operators.push(
                  getOperator(
                    "order_order",
                    "status_code",
                    "EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.ORDER_PREPARED),
                    "status_code"
                  )
                );
              }
              if (filter.statusPhases === "IN_PROGRESS") {
                operators.push(
                  getOperator(
                    "order_order_status",
                    "internal_code",
                    "EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.DELEVERY),
                    "internal_code"
                  )
                );
                operators.push(
                  getOperator(
                    "order_order",
                    "status_code",
                    "NOT_EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.ORDER_PREPARED),
                    "status_code"
                  )
                );
              }
              if (filter.statusPhases === "DONE") {
                 operators.push(
                  getOperator(
                    "order_order_status",
                    "internal_code",
                    "EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.ARCHIVING),
                    "internal_code"
                  )
                );
              }
              break;
            
              /* Phase 6-ARCHIVING*/ /*VN +VO*/
            case OrderStatusEnum.ARCHIVING:
              if (filter.statusPhases === "TODO") {
                operators.push(
                  getOperator(
                    "order_order_status",
                    "internal_code",
                    "EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.ARCHIVING),
                    "internal_code"
                  )
                );
                operators.push(
                  getOperator(
                    "order_order",
                    "status_code",
                    "EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.ORDER_POST_DELIVERED),
                    "status_code"
                  )
                );
              }
              if (filter.statusPhases === "IN_PROGRESS") {
                operators.push(
                  getOperator(
                    "order_order_status",
                    "internal_code",
                    "EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.ARCHIVING),
                    "internal_code"
                  )
                );
                operators.push(
                  getOperator(
                    "order_order",
                    "status_code",
                    "NOT_EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.ORDER_POST_DELIVERED),
                    "status_code"
                  )
                );
              }
              if (filter.statusPhases === "DONE") {
                 operators.push(
                  getOperator(
                    "order_order_status",
                    "internal_code",
                    "EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.ARCHIVING),
                    "internal_code"
                  )
                );
                operators.push(
                  getOperator(
                    "order_order",
                    "status_code",
                    "EQUAL",
                    globalConfig.formatters.getEqualValue(OrderStatusEnum.ARCHIVING),
                    "status_code"
                  )
                );
              }
              break;
      }
     }

  }
  if (filter.manufacturingNumber) {
    pushMultiValueLikeFilter(operators, multiValueGroups, filter.manufacturingNumber, "lower(order_order_asset", "num_fabrication)", "num_fabrication");
  }
  if (filter.registrationNumber) {
    pushMultiValueLikeFilter(operators, multiValueGroups, filter.registrationNumber, "lower(order_order_asset", "matriculation)", "matriculation");
  }
  if (filter.activity !== undefined) {
    operators.push(
      getOperator(
        "order_order_asset",
        "flag_new",
        "EQUAL",
        globalConfig.formatters.getEqualValue(filter.activity),
        "flag_new"
      )
    );
  }
  if (filter.delivered) {
    operators.push(
      getOperator("order_order", "effective_delivery_date", "ISNOTNULL")
    );
  }
  if (filter.notDelivered) {
    operators.push(
      getOperator("order_order", "effective_delivery_date", "ISNULL")
    );
  }
  if (filter.brand && filter.brand.length > 0) {
    const brandIds: string[] = Array.isArray(filter.brand) ? filter.brand : [filter.brand];
    const filteredBrandArray: string[] = [];
    for (const brandId of brandIds) {
      const brandModel = brandId.split('asset.good.vehicle.brand.')[1] || undefined;
      const brandWithout_ = brandModel ? brandModel.replace("_", " ") : undefined;
      [brandId, brandModel, brandWithout_].forEach(v => { if (v && !filteredBrandArray.includes(v)) filteredBrandArray.push(v); });
    }
    operators.push(
      operator(
        {
          val: filteredBrandArray,
          table: "trim(order_order_asset",
          dbField: "brand)"
        }
      ).IN
    );
      //}
  }
  if (filter.model) {
    const range = filter.model && filter.model.includes('range.') ? filter.model.substring(filter.model.lastIndexOf(".") + 1) : undefined;
    const rangeWithout_ = range.replace("_", " "); 
      const filteredRangeArray =[filter.model,range,rangeWithout_].filter(item => item !== undefined);
    operators.push(
        operator(
            {
                val: filteredRangeArray,
                table: "trim(order_order_asset",
                dbField: "_range)"
            }
        ).IN
    );
  }
  if (filter.chassisReference) {
    pushMultiValueLikeFilter(operators, multiValueGroups, filter.chassisReference, "lower(order_order_asset", "chassis_reference)", "chassis_reference");
  }
  if (filter.orderAlertsList&& filter.orderAlertsList.length > 0) {
    operators.push(
      getInOperator(
        "order_order_alert_reason",
        "reason_code",
        "IN",
        null,
        null,
        globalConfig.formatters.getInValue(filter.orderAlertsList)
      )
    );
    operators.push(getOperator("order_order_alert", "alert_validity_until", "ISNULL"));
  }
  if (filter.orderConditionsList&& filter.orderConditionsList.length > 0) {
    operators.push(
      getInOperator(
        "order_condition",
        "condition_code",
        "IN",
        null,
        null,
        globalConfig.formatters.getInValue(filter.orderConditionsList)
      )
    );
  }

  if (filter.orderConditionsListToggle !== undefined && filter.orderConditionsListToggle !== null) {
    operators.push(
      getOperator(
        "order_condition",
        "checked",
        "EQUAL",
        globalConfig.formatters.getEqualValue(filter.orderConditionsListToggle === true ? "true" : "false"),
        "checked"
      )
    );
  }

  if (filter.contractStatus) {
    operators.push(
      getOperator(
        "order_order_item",
        "status_code",
        "EQUAL",
        globalConfig.formatters.getEqualValue(filter.contractStatus),
        "status_code"
      )
    );
  }

  if (filter.deliveryDateFrom) {
    operators.push(
      getOperator(
        "cast(order_order_item",
        "initial_delivery_date AS date)",
        "GREATER_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.deliveryDateFrom,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "deliveryDateFrom"
      )
    );
  }
  if (filter.deliveryDateTo) {
    operators.push(
      getOperator(
        "cast(order_order_item",
        "initial_delivery_date  AS date)",
        "LESS_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.deliveryDateTo,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "deliveryDateTo"
      )
    );
  }
  if (filter.deliveryDateToggle !== undefined && filter.deliveryDateToggle !== null) {
    operators.push(
      getOperator(
        "order_order_item",
        "initial_delivery_date",
        filter.deliveryDateToggle === true ? "ISNOTNULL" : "ISNULL"
      )
    );
  }

  if (filter.deliveryDateUsineFrom) {
    operators.push(
      getOperator(
        "order_order_asset",
        "desired_delivery_factory_date",
        "GREATER_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.deliveryDateUsineFrom,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "deliveryDateUsineFrom"
      )
    );
  }
  if (filter.deliveryDateUsineTo) {
    operators.push(
      getOperator(
        "order_order_asset",
        "desired_delivery_factory_date",
        "LESS_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.deliveryDateUsineTo,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "deliveryDateUsineTo"
      )
    );
  }
  if (filter.deliveryDateUsineToggle !== undefined && filter.deliveryDateUsineToggle !== null) {
    operators.push(
      getOperator(
        "order_order_asset",
        "desired_delivery_factory_date",
        filter.deliveryDateUsineToggle === true ? "ISNOTNULL" : "ISNULL"
      )
    );
  }

  if (filter.archivingDateFrom) {
    operators.push(
      getOperator(
        "cast(order_order_item",
        "archiving_date AS date)",
        "GREATER_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.archivingDateFrom,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "archivingDateFrom"
      )
    );
  }
  if (filter.archivingDateTo) {
    operators.push(
      getOperator(
        "cast(order_order_item",
        "archiving_date AS date)",
        "LESS_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.archivingDateTo,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "archivingDateTo"
      )
    );
  }
  if (filter.archivingDateToggle !== undefined && filter.archivingDateToggle !== null) {
    operators.push(
      getOperator(
        "order_order_item",
        "archiving_date",
        filter.archivingDateToggle === true ? "ISNOTNULL" : "ISNULL"
      )
    );
  }

  if (filter.previsonalDeliveryDateFrom) {
    operators.push(
      getOperator(
        "cast(order_order_item",
        "prevision_delivery_date  AS date)",
        "GREATER_THAN_EQUAL",
         globalConfig.formatters.getEqualValue(moment(filter.previsonalDeliveryDateFrom,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "previsonalDeliveryDateFrom"
      )
    );
  }
  if (filter.previsonalDeliveryDateTo) {
    operators.push(
      getOperator(
        "cast(order_order_item",
        "prevision_delivery_date  AS date)",
        "LESS_THAN_EQUAL",
        
         globalConfig.formatters.getEqualValue(moment(filter.previsonalDeliveryDateTo,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "previsonalDeliveryDateTo"
      )
    );
  }
  if (filter.previsonalDeliveryDateToggle !== undefined && filter.previsonalDeliveryDateToggle !== null) {
    operators.push(
      getOperator(
        "order_order_item",
        "prevision_delivery_date",
        filter.previsonalDeliveryDateToggle === true ? "ISNOTNULL" : "ISNULL"
      )
    );
  }
  if (filter.previsonalDeliveryDateGreaterThanTodayToggle !== undefined && filter.previsonalDeliveryDateGreaterThanTodayToggle !== null && filter.previsonalDeliveryDateGreaterThanTodayToggle === true) {
    operators.push(
      getOperator(
        "cast(order_order_item",
        "prevision_delivery_date AS date)",
        "GREATER_THAN_EQUAL",
          globalConfig.formatters.getEqualValue(moment().format('YYYY-MM-DD')),
        "previsonalDeliveryDateGreaterThanTodayToggle"
      )
    );
  }
    if (filter.previsonalDeliveryDateInferiorThanTodayToggle !== undefined && filter.previsonalDeliveryDateInferiorThanTodayToggle !== null && filter.previsonalDeliveryDateInferiorThanTodayToggle === true) {
        operators.push(
            getOperator(
                "cast(order_order_item",
                "prevision_delivery_date AS date)",
                "LESS_THAN",
                globalConfig.formatters.getEqualValue(moment().format('YYYY-MM-DD')),
                "previsonalDeliveryDateInferiorThanTodayToggle"
            )
        );
    }

  if (filter.realDeliveryDateFrom) {
    operators.push(
      getOperator(
        "cast(order_order_item",
        "effective_delivery_date  AS date)",
        "GREATER_THAN_EQUAL",
         globalConfig.formatters.getEqualValue(moment(filter.realDeliveryDateFrom,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "realDeliveryDateFrom"
      )
    );
  }
  if (filter.realDeliveryDateTo) {
    operators.push(
      getOperator(
        "cast(order_order_item",
        "effective_delivery_date  AS date)",
        "LESS_THAN_EQUAL",
         globalConfig.formatters.getEqualValue(moment(filter.realDeliveryDateTo,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "realDeliveryDateTo"
      )
    );
  }
  if (filter.realDeliveryDateToggle !== undefined && filter.realDeliveryDateToggle !== null) {
    operators.push(
      getOperator(
        "order_order_item",
        "effective_delivery_date",
        filter.realDeliveryDateToggle === true ? "ISNOTNULL" : "ISNULL"
      )
    );
  }
  if (filter.effectiveDeliveryDateFrom) {
    operators.push(
      getOperator(
        "order_order_asset",
        "statement_delivery_date",
        "GREATER_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.effectiveDeliveryDateFrom,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "statement_delivery_dateFrom"
      )
    );
  }
  if (filter.effectiveDeliveryDateTo) {
    operators.push(
      getOperator(
        "order_order_asset",
        "statement_delivery_date",
        "LESS_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.effectiveDeliveryDateTo,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "statement_delivery_dateTo"
      )
    );
  }
  if (filter.effectiveDeliveryDateToggle !== undefined && filter.effectiveDeliveryDateToggle !== null) {
    operators.push(
      getOperator(
        "order_order_asset",
        "statement_delivery_date",
        filter.effectiveDeliveryDateToggle === true ? "ISNOTNULL" : "ISNULL"
      )
    );
  }

  if (filter.manufacturerInvoiceDateFrom) {
    operators.push(
      getOperator(
        "order_order_item",
        "manufacturer_invoice_date",
        "GREATER_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.manufacturerInvoiceDateFrom,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "manufacturer_invoice_date"
      )
    );
  }
  if (filter.manufacturerInvoiceDateTo) {
    operators.push(
      getOperator(
        "order_order_item",
        "manufacturer_invoice_date",
        "LESS_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.manufacturerInvoiceDateTo,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "manufacturer_invoice_date"
      )
    );
  }
  if (filter.manufacturerInvoiceDateToggle !== undefined && filter.manufacturerInvoiceDateToggle !== null) {
    operators.push(
      getOperator(
        "order_order_item",
        "manufacturer_invoice_date",
        filter.manufacturerInvoiceDateToggle === true ? "ISNOTNULL" : "ISNULL"
      )
    );
  }

  if (filter.effectiveDeliveryFactoryDateFrom) {
    operators.push(
      getOperator(
        "order_order_asset",
        "effective_delivery_factory_date",
        "GREATER_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.effectiveDeliveryFactoryDateFrom,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "effectiveDeliveryFactoryDateFrom"
      )
    );
  }
  if (filter.effectiveDeliveryFactoryDateTo) {
    operators.push(
      getOperator(
        "order_order_asset",
        "effective_delivery_factory_date",
        "LESS_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.effectiveDeliveryFactoryDateTo,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "effectiveDeliveryFactoryDateTo"
      )
    );
  }
  if (filter.effectiveDeliveryFactoryDateToggle !== undefined && filter.effectiveDeliveryFactoryDateToggle !== null) {
    operators.push(
      getOperator(
        "order_order_asset",
        "effective_delivery_factory_date",
        filter.effectiveDeliveryFactoryDateToggle === true ? "ISNOTNULL" : "ISNULL"
      )
    );
  }
  if (filter.factoryOrderDateFrom) {
    operators.push(
      getOperator(
        "order_order_delivery_details",
        "factory_order_date",
        "GREATER_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.factoryOrderDateFrom,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "factoryOrderDateFrom"
      )
    );
  }
  if (filter.factoryOrderDateTo) {
    operators.push(
      getOperator(
        "order_order_delivery_details",
        "factory_order_date",
        "LESS_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.factoryOrderDateTo,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "factoryOrderDateTo"
      )
    );
  }
  if (filter.factoryOrderDateToggle !== undefined && filter.factoryOrderDateToggle !== null) {
    operators.push(
      getOperator(
        "order_order_delivery_details",
        "factory_order_date",
        filter.factoryOrderDateToggle === true ? "ISNOTNULL" : "ISNULL"
      )
    );
  }
  if (filter.customerAppointmentDateFrom) {
    operators.push(
      getOperator(
        "order_order_item",
        "customer_appointment_date",
        "GREATER_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.customerAppointmentDateFrom,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "customerAppointmentDateFrom"
      )
    );
  }
  if (filter.customerAppointmentDateTo) {
    operators.push(
      getOperator(
        "order_order_item",
        "customer_appointment_date",
        "LESS_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.customerAppointmentDateTo,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "customerAppointmentDateTo"
      )
    );
  }
  if (filter.chassis_NumberToggle !== undefined && filter.chassis_NumberToggle !== null) {
        if (filter.chassis_NumberToggle === true){
          operators.push(
            getOperator(
              "order_order_asset", 
              "chassis_reference", 
              "NOT_EQUAL", 
              "''", 
              "chassis_reference"))
          }
        else if (filter.chassis_NumberToggle === false){
          operatorsChassisRererence.push(
            getOperator(
              "order_order_asset",
              "chassis_reference",
              "ISNULL"
            )
          );
          operatorsChassisRererence.push(
            getOperator(
              "order_order_asset", 
              "chassis_reference", 
              "EQUAL", 
              "''", 
              "chassis_reference"))
        }
  }
  if (filter.forced_DocumentsToggle !== undefined && filter.forced_DocumentsToggle !== null) {
        if (filter.forced_DocumentsToggle === true){
          operators.push(
            getOperator(
              "received_with_forced_doc",
              "nbr_forced_doc",
              "GREATER_THAN",
              globalConfig.formatters.getEqualValue(0),
              "nbr_forced_doc"
            )
          );
        }
        else if (filter.forced_DocumentsToggle === false){
          operators.push(
              getOperator(
                "received_with_forced_doc",
                "nbr_forced_doc",
                "EQUAL",
                globalConfig.formatters.getEqualValue(0),
                "nbr_forced_doc"
              )
            );
        }
  }
  if (filter.sAnnonceeSuperiorToDllToggle !== undefined && filter.sAnnonceeSuperiorToDllToggle !== null && filter.sAnnonceeSuperiorToDllToggle === true) {
    operators.push(
      getOperator(
        "order_order_asset",
        "prevision_delivery_factory_date",
        "GREATER_THAN",
        "order_order_item.prevision_delivery_date",
        "sAnnonceeSuperiorToDllToggle"
      )
    );
  }
  if (filter.customerAppointmentDateToggle !== undefined && filter.customerAppointmentDateToggle !== null) {
    operators.push(
      getOperator(
        "order_order_item",
        "customer_appointment_date",
        filter.customerAppointmentDateToggle === true ? "ISNOTNULL" : "ISNULL"
      )
    );
  }
  if (filter.supplierName) {
    operators.push(
      getOperator(
        "lower(party_party",
        "_name)",
        "LIKE",
        globalConfig.formatters
          .getEqualValue("%" + filter.supplierName + "%")
          .toLowerCase(),
        "_name"
      )
    );
  }
  if (filter.clientReference) {
    pushMultiValueLikeFilter(operators, multiValueGroups, filter.clientReference, "lower(party_party_1", "_reference)", "_reference");
  }
  if (filter.family_name) {
    operators.push(
      getOperator(
        "lower(party_party_1",
        "family_name)",
        "LIKE",
        globalConfig.formatters
          .getEqualValue("%"+ filter.family_name + "%")
          .toLowerCase(),
        "family_name"
      )
    );
  }
  if (filter.firstName) {
    operators.push(
      getOperator(
        "lower(party_party_1",
        "first_name)",
        "LIKE",
        globalConfig.formatters
          .getEqualValue("%"+ filter.firstName + "%")
          .toLowerCase(),
        "first_name"
      )
    );
  }
  if (filter.commercial_name) {
    operators.push(
      getOperator(
        "lower(party_party_1",
        "commercial_name)",
        "LIKE",
        globalConfig.formatters
          .getEqualValue("%"+ filter.commercial_name + "%")
          .toLowerCase(),
        "commercial_name"
      )
    );
  }
    if (filter.name) {
      // EFLO-793: the name is searched on the client and on the end client (order_locataire subquery)
      clientNameFilter = buildMultiClientNameFilter(filter.name, ["party_party_1", "order_locataire"]);
    }

  if (filter.partyRole && filter.partyRole.length > 0) {

      operators.push(
       getOperator(
         "lower(order_party_actor",
         "role_code)",
         "EQUAL",
         globalConfig.formatters.getEqualValue(filter.partyRole),
         "partyRole"
       )
     );

     if (filter.partyName && filter.partyName.length > 0) {
       const partyNameList = Array.isArray(filter.partyName)
        ? filter.partyName
            .filter((item:any): item is string => typeof item === "string" && item.trim() !== "")
            .map((item:any) => item.toLowerCase())
        : [];
          operators.push(
            buildGroupedConditions([
              [getInOperator("lower(order_party_actor", "full_name)", "IN", null, null, globalConfig.formatters.getInValue(partyNameList))],
              [getInOperator("lower(order_party_actor", "_name)", "IN", null, null, globalConfig.formatters.getInValue(partyNameList))],
            ])
          );
     }

  } 
  // Statut préconditions avec « Document obligatoire » « Document Non obligatoire » et « Tous ».
  if (filter.preconditionsStatus) {
    operators.push(
      getOperator(
        "supportingdocument_check_points",
        "status_code",
        "EQUAL",
        globalConfig.formatters.getEqualValue(filter.preconditionsStatus),
        "status_code"
    )
    );
  }
  if (filter.mandatory_DocumentsToggle !== undefined && filter.mandatory_DocumentsToggle !== null && !filter.documentList?.length) {
        operators.push(
          getOperator(
            "supportingdocument_check_points",
            "mandatory_docs_complete",
            "EQUAL",
            globalConfig.formatters.getEqualValue(filter.mandatory_DocumentsToggle === true ? "true" : "false"),
            "mandatoryDocuments"
          )
        );
  }
  if (filter.financingType) {
    operators.push(
      getOperator(
        "order_order_financing",
        "label",
        "EQUAL",
        globalConfig.formatters.getEqualValue(filter.financingType),
        "label"
      )
    );
  }
  if (filter.preconditionsList&&filter.preconditionsList.length>0) {
    operators.push(getInOperator("supportingdocument_check_points", "check_points_config_code", "IN", null, null, globalConfig.formatters.getInValue(filter.preconditionsList)))

  }
  if (filter.documentList&&filter.documentList.length>0) {  
    operators.push(getInOperator("supportingdocument_supporting_type", "document_type_id", "IN", null, null, globalConfig.formatters.getInValue(filter.documentList)))
    
  }
  if (filter.preconditionDateFrom) {
    operators.push(
      getOperator(
        "cast(supportingdocument_check_points",
        "completion_date  AS date)",
        "GREATER_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.preconditionDateFrom,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "completion_date"
      )
    );
  }
  if (filter.preconditionDateTo) {
    operators.push(
      getOperator(
        "cast(supportingdocument_check_points",
        "completion_date  AS date)",
        "LESS_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.preconditionDateTo,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "_when_created"
      )
    );
  }
  if (filter.preconditionDateToggle !== undefined && filter.preconditionDateToggle !== null) {
    operators.push(
      getOperator(
        "supportingdocument_received_file",
        "reception_date",
        filter.preconditionDateToggle === true ? "ISNOTNULL" : "ISNULL"
      )
    );
  }
  if (filter.initialDeliveryFactoryDateFrom) {
    operators.push(
      getOperator(
        "cast(order_order_asset",
        "initial_delivery_factory_date  AS date)",
        "GREATER_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.initialDeliveryFactoryDateFrom,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "initial_delivery_factory_date"
      )
    );
  }
  if (filter.initialDeliveryFactoryDateTo) {
    operators.push(
      getOperator(
        "cast(order_order_asset",
        "initial_delivery_factory_date  AS date)",
        "LESS_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.initialDeliveryFactoryDateTo,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "initial_delivery_factory_date"
      )
    );
  }
  if (filter.initialDeliveryFactoryDateToggle !== undefined && filter.initialDeliveryFactoryDateToggle !== null) {
    operators.push(
      getOperator(
        "order_order_asset",
        "initial_delivery_factory_date",
        filter.initialDeliveryFactoryDateToggle === true ? "ISNOTNULL" : "ISNULL"
      )
    );
  }

  if (filter.availabilityCarDateFrom) {
    operators.push(
      getOperator(
        "cast(order_order_asset",
        "availability_car_date  AS date)",
        "GREATER_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.availabilityCarDateFrom,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "availability_car_date"
      )
    );
  }
  if (filter.availabilityCarDateTo) {
    operators.push(
      getOperator(
        "cast(order_order_asset",
        "availability_car_date  AS date)",
        "LESS_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.availabilityCarDateTo,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "availability_car_date"
      )
    );
  }
  if (filter.availabilityCarDateToggle !== undefined && filter.availabilityCarDateToggle !== null) {
    operators.push(
      getOperator(
        "order_order_asset",
        "availability_car_date",
        filter.availabilityCarDateToggle === true ? "ISNOTNULL" : "ISNULL"
      )
    );
  }
 
  if (filter.immatriculationDateFrom) {
    operators.push(
      getOperator(
        "order_order_asset",
        "matriculation_date",
        "GREATER_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.immatriculationDateFrom,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "immatriculationDateFrom"
      )
    );
  }
  if (filter.immatriculationDateTo) {
    operators.push(
      getOperator(
        "order_order_asset",
        "matriculation_date",
        "LESS_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.immatriculationDateTo,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "immatriculationDateTo"
      )
    );
  }
  if (filter.immatriculationDateToggle !== undefined && filter.immatriculationDateToggle !== null) {
    operators.push(
      getOperator(
        "order_order_asset",
        "matriculation_date",
        filter.immatriculationDateToggle === true ? "ISNOTNULL" : "ISNULL"
      )
    );
  }
 
  if (filter.assignableFromDateFrom) {
    operators.push(
      getOperator(
        "order_order_asset",
        "assignable_from_date",
        "GREATER_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.assignableFromDateFrom,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "assignableFromDateFrom"
      )
    );
  }
  if (filter.assignableFromDateTo) {
    operators.push(
      getOperator(
        "order_order_asset",
        "assignable_from_date",
        "LESS_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.assignableFromDateTo,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "assignableFromDateTo"
      )
    );
  }
  if (filter.assignableFromDateToggle !== undefined && filter.assignableFromDateToggle !== null) {
    operators.push(
      getOperator(
        "order_order_asset",
        "assignable_from_date",
        filter.assignableFromDateToggle === true ? "ISNOTNULL" : "ISNULL"
      )
    );
  }
 
  if (filter.maxMatriculationDateFrom) {
    operators.push(
      getOperator(
        "order_order_delivery_details",
        "max_matriculation_date",
        "GREATER_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.maxMatriculationDateFrom,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "maxMatriculationDateFrom"
      )
    );
  }
  if (filter.maxMatriculationDateTo) {
    operators.push(
      getOperator(
        "order_order_delivery_details",
        "max_matriculation_date",
        "LESS_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.maxMatriculationDateTo,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "maxMatriculationDateTo"
      )
    );
  }
  if (filter.maxMatriculationDateToggle !== undefined && filter.maxMatriculationDateToggle !== null) {
    operators.push(
      getOperator(
        "order_order_delivery_details",
        "max_matriculation_date",
        filter.maxMatriculationDateToggle === true ? "ISNOTNULL" : "ISNULL"
      )
    );
  }
 
  if (filter.minDeliveryDateFrom) {
    operators.push(
      getOperator(
        "order_order_delivery_details",
        "min_delivery_date",
        "GREATER_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.minDeliveryDateFrom,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "minDeliveryDateFrom"
      )
    );
  }
  if (filter.minDeliveryDateTo) {
    operators.push(
      getOperator(
        "order_order_delivery_details",
        "min_delivery_date",
        "LESS_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.minDeliveryDateTo,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "minDeliveryDateTo"
      )
    );
  }
  if (filter.minDeliveryDateToggle !== undefined && filter.minDeliveryDateToggle !== null) {
    operators.push(
      getOperator(
        "order_order_delivery_details",
        "min_delivery_date",
        filter.minDeliveryDateToggle === true ? "ISNOTNULL" : "ISNULL"
      )
    );
  }
 
  if (filter.maxDeliveryDateFrom) {
    operators.push(
      getOperator(
        "order_order_delivery_details",
        "max_delivery_date",
        "GREATER_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.maxDeliveryDateFrom,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "maxDeliveryDateFrom"
      )
    );
  }
  if (filter.maxDeliveryDateTo) {
    operators.push(
      getOperator(
        "order_order_delivery_details",
        "max_delivery_date",
        "LESS_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.maxDeliveryDateTo,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "maxDeliveryDateTo"
      )
    );
  }
  if (filter.maxDeliveryDateToggle !== undefined && filter.maxDeliveryDateToggle !== null) {
    operators.push(
      getOperator(
        "order_order_delivery_details",
        "max_delivery_date",
        filter.maxDeliveryDateToggle === true ? "ISNOTNULL" : "ISNULL"
      )
    );
  }
 
  if (filter.cancellationDateFrom) {
    operators.push(
      getOperator(
        "order_order_item",
        "cancellation_date",
        "GREATER_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.cancellationDateFrom,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "cancellationDateFrom"
      )
    );
  }
  if (filter.cancellationDateTo) {
    operators.push(
      getOperator(
        "order_order_item",
        "cancellation_date",
        "LESS_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.cancellationDateTo,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "cancellationDateDateTo"
      )
    );
  }
  if (filter.cancellationDateToggle !== undefined && filter.cancellationDateToggle !== null) {
    operators.push(
      getOperator(
        "order_order_item",
        "cancellation_date",
        filter.cancellationDateToggle === true ? "ISNOTNULL" : "ISNULL"
      )
    );
  }
  if (filter.orderCreatedDateFrom) {
    operators.push(
      getOperator(
        "cast(order_order",
        "_when_created  AS date)",
        "GREATER_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.orderCreatedDateFrom,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "_when_created"
      )
    );
  }
  if (filter.orderCreatedDateTo) {
    operators.push(
      getOperator(
        "cast(order_order",
        "_when_created  AS date)",
        "LESS_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.orderCreatedDateTo,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "_when_created"
      )
    );
  }
  if (filter.orderCreatedDateToggle !== undefined && filter.orderCreatedDateToggle !== null) {
    operators.push(
      getOperator(
        "order_order",
        "_when_created",
        filter.orderCreatedDateToggle === true ? "ISNOTNULL" : "ISNULL"
      )
    );
  }
  if (filter.networkNode) {
    operators.push(
      getOperator(
        "party_network_node",
        "id",
        "EQUAL",
        globalConfig.formatters.getEqualValue(filter.networkNode),
        "party_network_node"
      )
    );
  }
  if (!filter.tradeInImmatriculation) {
    operators.push(
      getOperator("order_order_asset", "asset_trade_in_id", "ISNULL")
    );
  }
  if (filter.tradeInImmatriculation) {
    operators.push(
      getOperator("order_order_asset", "asset_trade_in_id", "ISNOTNULL")
    );
    operators.push(
      getOperator(
        "lower(order_order_asset",
        "matriculation)",
        "LIKE",
        globalConfig.formatters
          .getEqualValue("%" + filter.tradeInImmatriculation + "%")
          .toLowerCase(),
        "matriculation"
      )
    );
  }
  if (filter.notificationFilter) {
    const filterValue = globalConfig.formatters.getEqualValue(filter.notificationFilter).toLowerCase();
    operatorsNotificationFilter.push(
      getOperator(
        "lower(order_order_item",
        "external_reference)",
        "LIKE",
        filterValue,
        "external_reference"
      )
    );
    operatorsNotificationFilter.push(
      getOperator(
        "lower(order_order",
        "_reference)",
        "LIKE",
        filterValue,
        "_reference"
      )
    );
  }
  if (filter.initialSort) {

    if (filter.initialSort.sortBy ==='ots'){
        qc_header.qc.otherOrders = {
          fields: [
            {
              table: "order_order_ots_history" ,
              name: "ots_count",
              orientation: filter.initialSort.orientation 
            }
          ]
        };
    }
    else {
       qc_header.qc.otherOrders = {
          fields: [
            {
              table: "order_order" ,
              name: "_when_created",
              orientation: "DESC"
            }
          ]
        };
    } 

  }  
    

      // Groups are pushed at the end of the array: the dataservice engine does
      // not emit the binary operator for a plain expression placed after a group
      // among its siblings (QueryParser.buildFilterClause).
      operators.push(...multiValueGroups);
      if ( clientNameFilter ){
            operators.push(clientNameFilter)
      }
      if (rdvOutsideWindowFilter) {
        operators.push(rdvOutsideWindowFilter)
      }
      if ( operatorsNotificationFilter.length> 0 ){
        operators.push( { or: {
          operators :[... operatorsNotificationFilter]
        } 
      })
     }
    if ( operatorsChassisRererence.length> 0 ){
      operators.push( { or: {
        operators :[... operatorsChassisRererence]
      } 
     })
    }
    if (operators.length > 0 ) {
          qc_header.qc.otherFilters = {
            expressions: [
              {
                and: {
                  operators,
                },
              
              },
            ],
          };
        }

        


  
  return qc_header.qc;
}

export function getQcOrderQuickSearch(
  filter: {
    searchValue: string;
    activity?: boolean | null;
    userNetwork?: any;
    notificationFilter?: any;
    isRoleAdmin?: boolean;
  },
  offset?: any,
  limit?: any
) {
  const qc_header: any = {
    qc: {
      queryId: "order-search-filter-backup",
      offset: offset || 0,
      limit: limit || 100,
      parameters: {
        ...filter,
      },
    },
  };

  const searchValue = (filter?.searchValue ?? "").trim();
  const andOperators: any[] = [];

  const roleUser = getRoleUser();
  if (
    !roleUser.isCDV &&
    !roleUser.isDirector &&
    !roleUser.isSecretary &&
    !roleUser.isAuditor &&
    !(filter && (filter as any).notificationFilter) &&
    !(filter as any).isRoleAdmin
  ) {
    andOperators.push(buildOrderVisibilityOperator(roleUser, qc_header.qc.queryId));
  }

  if (filter && filter.userNetwork) {
    andOperators.push(
      getOperator(
        "party_network_node",
        "id",
        "EQUAL",
        globalConfig.formatters.getEqualValue(filter.userNetwork.value),
        "party_network_node"
      )
    );
  }

  if (filter.activity !== undefined && filter.activity !== null) {
    andOperators.push(
      getOperator(
        "order_order_asset",
        "flag_new",
        "EQUAL",
        globalConfig.formatters.getEqualValue(String(filter.activity)),
        "activity"
      )
    );
  }

  andOperators.push(
      getOperator("order_order_asset", "asset_trade_in_id", "ISNULL")
    );

  if (searchValue) {
    const like = globalConfig.formatters.getEqualValue(`%${searchValue}%`).toLowerCase();
    const orOperators = [
      [getOperator("lower(order_order_item", "external_reference)", "LIKE", like, "externalReference")],
      [getOperator("lower(order_order_asset", "num_fabrication)", "LIKE", like, "manufacturingNumber")],
      [getOperator("lower(order_order_asset", "matriculation)", "LIKE", like, "registrationNumber")],
      [getOperator("lower(order_order_asset", "chassis_reference)", "LIKE", like, "chassisReference")],
      [getOperator("lower(order_order", "_reference)", "LIKE", like, "filterReference")],
    ];

    andOperators.push(buildGroupedConditions(orOperators));
  }

  if (andOperators.length > 0) {
    qc_header.qc.otherFilters = {
      expressions: [
        {
          and: {
            operators: andOperators,
          },
        },
      ],
    };
  }

  return qc_header.qc;
}
export function getQcOrderDeliverySchedule(filter: any,offset?: any, limit?: any) {
  const qc_header: any = {
    qc: {
      queryId: "order-search-filter-deliveryschedule",
      offset: offset || 0,
      limit: limit || 100,
    },
  };
  
  qc_header.qc.parameters = {
    ...filter,
  };
  const operators = [];
  let clientNameFilter: any = null;

  if (filter.assignedUser) {
    operators.push(
      getOperator(
        "order_assigned_user",
        "user_resourceuid",
        "EQUAL",
        globalConfig.formatters.getEqualValue(filter.assignedUser),
        "assignedUser"
      )
    );
  }

  if (filter && filter.planningType) {
    operators.push(getOperator("order_order_planning_type","internal_code","EQUAL",globalConfig.formatters.getEqualValue(filter.planningType),"planningType"));
    if (filter && filter.category) {
      operators.push(getOperator("order_order_planning_type","id","EQUAL",globalConfig.formatters.getEqualValue(filter.category),"category"));
    }
  }

  if(filter.planingEvent){
    operators.push(getInOperator("order_order", "status_code", "IN", null, null, globalConfig.formatters.getInValue(['DELIVERED','PLANNED','TO_PLAN','TO_BE_RESCHEDULED'])))
  }
    if (filter.orderPlanningId) {
    operators.push(
      getOperator(
        "order_order_planning",
        "id",
        "EQUAL",
        globalConfig.formatters.getEqualValue(filter.orderPlanningId),
        "id"
      )
    );
  }

  if (filter.filterReference) {
    operators.push(
      getOperator(
        "lower(order_order",
        "reference)",
        "LIKE",
        globalConfig.formatters
          .getEqualValue("%" + filter.filterReference + "%")
          .toLowerCase(),
        "reference"
      )
    );
  }
  if (filter.externalReference) {
    operators.push(
      getOperator(
        "lower(order_order_item",
        "external_reference)",
        "LIKE",
        globalConfig.formatters
          .getEqualValue("%" + filter.externalReference + "%")
          .toLowerCase(),
        "external_reference"
      )
    );
  }
  if (filter.filterStatus) {
    const filterStatusList = Array.isArray(filter.filterStatus)? filter.filterStatus: [filter.filterStatus];
    if (filterStatusList.length > 0) {
      operators.push(
        getInOperator(
          "order_order",
          "status_code",
          "IN",
          null,
          null,
          globalConfig.formatters.getInValue(filterStatusList)
        )
      );
    }
  }
  if (filter.manufacturingNumber) {
    operators.push(
      getOperator(
        "lower(order_order_asset",
        "num_fabrication)",
        "LIKE",
        globalConfig.formatters
          .getEqualValue("%" + filter.manufacturingNumber + "%")
          .toLowerCase(),
        "num_fabrication"
      )
    );
  }
  if (filter.registrationNumber) {
    operators.push(
      getOperator(
        "lower(order_order_asset",
        "matriculation)",
        "LIKE",
        globalConfig.formatters
          .getEqualValue("%" + filter.registrationNumber + "%")
          .toLowerCase(),
        "matriculation"
      )
    );
  }
  if (filter.activity !== undefined) {
    operators.push(
      getOperator(
        "order_order_asset",
        "flag_new",
        "EQUAL",
        globalConfig.formatters.getEqualValue(filter.activity),
        "flag_new"
      )
    );
  }
  if (filter.delivered) {
    operators.push(
      getOperator("order_order", "effective_delivery_date", "ISNOTNULL")
    );
  }
  if (filter.notDelivered) {
    operators.push(
      getOperator("order_order", "effective_delivery_date", "ISNULL")
    );
  }
  if (filter.brand) {
    operators.push(
      getOperator(
        "order_order_asset",
        "brand",
        "EQUAL",
        globalConfig.formatters.getEqualValue(filter.brand),
        "brand"
      )
    );
  }
  if (filter.model) {
    operators.push(
      getOperator(
        "order_order_asset",
        "range",
        "EQUAL",
        globalConfig.formatters.getEqualValue(filter.model),
        "range"
      )
    );
  }
  if (filter.chassisReference) {
    operators.push(
      getOperator(
        "lower(order_order_asset",
        "chassis_reference)",
        "LIKE",
        globalConfig.formatters
        .getEqualValue("%" + filter.chassisReference + "%")
        .toLowerCase(),
        "chassis_reference"
      )
    );
  }
  if (filter.contractStatus) {
    operators.push(
      getOperator(
        "order_order_item",
        "status_code",
        "EQUAL",
        globalConfig.formatters.getEqualValue(filter.contractStatus),
        "status_code"
      )
    );
  }

  if (filter.deliveryDateFrom) {
    operators.push(
      getOperator(
        "order_order_item",
        "initial_delivery_date",
        "GREATER_THAN_EQUAL",
        formatDateLocale(filter.deliveryDateFrom, "DD/MM/YYYY", "fr").toString,
        "deliveryDateFrom"
      )
    );
  }
  if (filter.deliveryDateTo) {
    operators.push(
      getOperator(
        "order_order_item",
        "initial_delivery_date",
        "LESS_THAN_EQUAL",
        formatDateLocale(filter.deliveryDateTo, "DD/MM/YYYY", "fr").toString,
        "deliveryDateTo"
      )
    );
  }
  if (filter.previsonalDeliveryDateFrom) {
    operators.push(
      getOperator(
        "order_order_item",
        "prevision_delivery_date",
        "GREATER_THAN_EQUAL",
        formatDateLocale(filter.previsonalDeliveryDateFrom, "DD/MM/YYYY", "fr")
          .toString,
        "previsonalDeliveryDateFrom"
      )
    );
  }
  if (filter.previsonalDeliveryDateTo) {
    operators.push(
      getOperator(
        "order_order_item",
        "prevision_delivery_date",
        "LESS_THAN_EQUAL",
        formatDateLocale(filter.previsonalDeliveryDateTo, "DD/MM/YYYY", "fr")
          .toString,
        "previsonalDeliveryDateTo"
      )
    );
  }
  if (filter.supplierName) {
    operators.push(
      getOperator(
        "lower(party_party",
        "name)",
        "LIKE",
        globalConfig.formatters
          .getEqualValue("%" + filter.supplierName + "%")
          .toLowerCase(),
        "name"
      )
    );
  }
  if (filter.clientReference) {
    operators.push(
      getOperator(
        "lower(party_party_1",
        "reference)",
        "LIKE",
        globalConfig.formatters
          .getEqualValue("%" + filter.clientReference + "%")
          .toLowerCase(),
        "reference"
      )
    );
  }
  if (filter.family_name) {
    operators.push(
      getOperator(
        "lower(party_party_1",
        "family_name)",
        "LIKE",
        globalConfig.formatters
          .getEqualValue("%"+ filter.family_name + "%")
          .toLowerCase(),
        "family_name"
      )
    );
  }
  if (filter.firstName) {
    operators.push(
      getOperator(
        "lower(party_party_1",
        "first_name)",
        "LIKE",
        globalConfig.formatters
          .getEqualValue("%"+ filter.firstName + "%")
          .toLowerCase(),
        "first_name"
      )
    );
  }
  if (filter.commercial_name) {
    operators.push(
      getOperator(
        "lower(party_party_1",
        "commercial_name)",
        "LIKE",
        globalConfig.formatters
          .getEqualValue("%"+ filter.commercial_name + "%")
          .toLowerCase(),
        "commercial_name"
      )
    );
  }
    if (filter.name) {
      clientNameFilter = buildClientNameFilter(filter.name, "party_party_1");
    }
  if (filter.partyRole && filter.partyRole.length > 0) {
    operators.push(
        getOperator(
            "order_order_party_1",
            "role_code",
            "EQUAL",
            globalConfig.formatters.getEqualValue(filter.partyRole),
            "role_code"
        )
    );
  } 
  if (filter.preconditionsStatus) {
    operators.push(
      getOperator(
        "supportingdocument_check_points",
        "status_code",
        "EQUAL",
        globalConfig.formatters.getEqualValue(filter.preconditionsStatus),
        "status_code"
    )
    );
  }
  if (filter.financingType) {
    operators.push(
      getOperator(
        "order_order_financing",
        "label",
        "EQUAL",
        globalConfig.formatters.getEqualValue(filter.financingType),
        "label"
      )
    );
  }
  if (filter.preconditionsList) {
    operators.push(
      getOperator(
        "supportingdocument_check_points",
        "check_points_config_code",
        "EQUAL",
        globalConfig.formatters.getEqualValue(filter.preconditionsList),
        "check_points_config_code"
    )
    );
  }
  if (filter.preconditionDate) {
    operators.push(
      getOperator(
        "cast(supportingdocument_received_file",
        "reception_date  AS date)",
        "EQUAL",
        globalConfig.formatters.getEqualValue(filter.preconditionDate),
        "reception_date"
    )
    );
  }
  if (filter.immatriculationDateFrom) {
    operators.push(
      getOperator(
        "order_order_asset",
        "matriculation_date",
        "GREATER_THAN_EQUAL",
        formatDateLocale(filter.immatriculationDateFrom, "DD/MM/YYYY", "fr").toString,
        "immatriculationDateFrom"
      )
    );
  }
  if (filter.immatriculationDateTo) {
    operators.push(
      getOperator(
        "order_order_asset",
        "matriculation_date",
        "LESS_THAN_EQUAL",
        formatDateLocale(filter.immatriculationDateTo, "DD/MM/YYYY", "fr").toString,
        "immatriculationDateTo"
      )
    );
  }
  if (filter.orderCreatedDate) {
    operators.push(
      getOperator(
        "cast(order_order",
        "_when_created  AS date)",
        "EQUAL",
        globalConfig.formatters.getEqualValue(filter.orderCreatedDate),
        "_when_created"
    )
    );
  }
  if (!filter.tradeInImmatriculation) {
    operators.push(
      getOperator("order_order_asset", "asset_trade_in_id", "ISNULL")
    );
  }
  if (filter.tradeInImmatriculation) {
    operators.push(
      getOperator("order_order_asset", "asset_trade_in_id", "ISNOTNULL")
    );
    operators.push(
      getOperator(
        "lower(order_order_asset",
        "matriculation)",
        "LIKE",
        globalConfig.formatters
          .getEqualValue("%" + filter.tradeInImmatriculation + "%")
          .toLowerCase(),
        "matriculation"
      )
    );
  }
  else {
    operators.push(
        getOperator(
            "order_order_party_1",
            "role_code",
            "EQUAL",
            "'CUSTOMER'",
            "role_code"
        )
    );
  }

      // Group pushed last (see QueryParser.buildFilterClause).
      if ( clientNameFilter ){
            operators.push(clientNameFilter)
      }

    if (operators.length > 0 ) {
          qc_header.qc.otherFilters = {
            expressions: [
              {
                and: {
                  operators,
                },
              
              },
            ],
          };
        }


  
  return qc_header.qc;
}
export function searchCount(filter: any): Promise<any[]> {
  // Redirect to batch function for better performance
  return batchSearchCount(filter);
}

// Batch query count implementation
interface BatchQueryRequest {
  key: string;
  filter: any;
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
}

let batchQueue: BatchQueryRequest[] = [];
let batchTimeout: any = null;
const BATCH_DELAY = 1000; // 1000ms delay to collect all filter requests into one batch

function executeBatch() {
  if (batchQueue.length === 0) return;

  const currentBatch = [...batchQueue];
  batchQueue = [];
  batchTimeout = null;

  const url = settings.api_batch_query_count_url + `?v=v2`;
  const selectedUserNetwork: any = store.state.authModule?.selectedUserNetwork
    && store.state.authModule?.selectedUserNetwork.value
    ? store.state.authModule?.selectedUserNetwork.value
    : "";
  const selectedUserNetworkFromLocalStorage = store.getters["authModule/getSelectedUserNetworkFromLocalStorage"];
  const userNetwork = selectedUserNetwork ? selectedUserNetwork : selectedUserNetworkFromLocalStorage;

  // Prepare batch payload
  const payload = currentBatch.map(item => {
    const filter = { ...item.filter };

    // Apply user network filter
    const expressions = filter?.businessData?.otherFilters?.expressions;
    if (Array.isArray(expressions)) {
      expressions.forEach(expression => {
        if (Array.isArray(expression?.and?.operators)) {
          expression.and.operators.forEach((op: any) => {
            if (op.expression?.field === "id" && op.expression?.table === "party_network_node" && op.expression?.value !== userNetwork && filter?.businessData?.queryId !== 'delegation-search-filter') {
              op.expression.value = `'${userNetwork}'`;
            }
          });
        }
      });
    }

    return {
      key: item.key,
      queryConfiguration: filter.businessData
    };
  });

  const _headers = headers();

  axios
    .post(url, payload, {
      headers: _headers,
    })
    .then((res) => {
      // Map results back to requesters
      const results = res.data;
      currentBatch.forEach(item => {
        const result = results.find((r: any) => r.key === item.key);
        if (result) {
          item.resolve({ count: result.count });
        } else {
          item.reject(new Error(`No result for key: ${item.key}`));
        }
      });
    })
    .catch((err) => {
      // Reject all requests in batch
      currentBatch.forEach(item => item.reject(err));
    });
}

let requestCounter = 0;

export function batchSearchCount(filter: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    const key = `query_${requestCounter++}_${Date.now()}`;

    batchQueue.push({
      key,
      filter,
      resolve,
      reject
    });

    // Only start timeout if not already running - this ensures all requests
    // within the delay window are batched into ONE single request
    if (!batchTimeout) {
      batchTimeout = setTimeout(executeBatch, BATCH_DELAY);
    }
  });
}

export function getQcOffer(filter: any,offset?: any, limit?: any) {
  const qc_header: any = {
    qc: {
      queryId: "offer-filter-search",
      offset: offset || 0,
      limit: limit || 100,
    },
  };

  qc_header.qc.parameters = {
    ...filter,
  };
  const operators = [];

  if (filter.demandStatus) {
    operators.push(
      getOperator(
        "offer_offer_status",
        "id",
        "EQUAL",
        globalConfig.formatters.getEqualValue(filter.demandStatus),
        "id"
      )
    );
  }
  if (filter.reference) {
    operators.push(
      getOperator(
        "lower(offer_offer",
        "_reference)",
        "LIKE",
        globalConfig.formatters
          .getEqualValue(filter.reference + "%")
          .toLowerCase(),
        "_reference"
      )
    );
  }
  if (filter.firstName) {
    operators.push(
      getOperator(
        "lower(party_party",
        "first_name)",
        "LIKE",
        globalConfig.formatters
          .getEqualValue(filter.firstName + "%")
          .toLowerCase(),
        "firstName"
      )
    );
  }
  if (filter.familyName) {
    operators.push(
      getOperator(
        "lower(party_party",
        "family_name)",
        "LIKE",
        globalConfig.formatters
          .getEqualValue(filter.familyName + "%")
          .toLowerCase(),
        "familyName"
      )
    );
  }
  if (filter.clientReference) {
    operators.push(
      getOperator(
        "lower(party_party",
        "_reference)",
        "LIKE",
        globalConfig.formatters
          .getEqualValue(filter.clientReference + "%")
          .toLowerCase(),
        "clientReference"
      )
    );
  }
  if (filter.from) {
    operators.push(
      getOperator(
        "offer_offer",
        "_when_created",
        "GREATER_THAN_EQUAL",
        formatDateLocale(filter.from, "DD/MM/YYYY", "fr").toString,
        "from"
      )
    );
  }
  if (filter.until) {
    operators.push(
      getOperator(
        "offer_offer",
        "_when_created",
        "LESS_THAN_EQUAL",
        formatDateLocale(filter.until, "DD/MM/YYYY", "fr").toString,
        "until"
      )
    );
  }
  if (filter.channel) {
    operators.push(
      getOperator(
        "offer_offer",
        "sales_channel_code",
        "EQUAL",
        globalConfig.formatters.getEqualValue(filter.channel.id),
        "channel"
      )
    );
  }
  if (filter.typeClient) {
    operators.push(
      getOperator(
        "party_party",
        "_discriminator",
        "EQUAL",
        globalConfig.formatters.getEqualValue(filter.typeClient.id),
        "typeClient"
      )
    );
  }
  if (filter.network) {
    operators.push(
      getOperator(
        "offer_offer",
        "associated_networks_id",
        "EQUAL",
        globalConfig.formatters.getEqualValue(filter.network.id),
        "network"
      )
    );
  }
  if (filter.networkNode) {
    operators.push(
      getOperator(
        "offer_offer",
        "associated_network_nodes_id",
        "EQUAL",
        globalConfig.formatters.getEqualValue(filter.networkNode.id),
        "networkNode"
      )
    );
  }
  if (filter._decisionAnalysisProcessId) {
    operators.push(
      getOperator(
        "offer_offer",
        "_decisionAnalysisProcessId",
        "EQUAL",
        globalConfig.formatters.getEqualValue(
          filter._decisionAnalysisProcessId.id
        ),
        "_decisionAnalysisProcessId"
      )
    );
  }
  if (filter.brand) {
    operators.push(
      getOperator(
        "offer_proposal_asset",
        "brand",
        "EQUAL",
        globalConfig.formatters.getEqualValue(filter.brand.id),
        "brand"
      )
    );
  }
  if (filter.range) {
    operators.push(
      getOperator(
        "offer_proposal_asset",
        "range",
        "EQUAL",
        globalConfig.formatters.getEqualValue(filter.range.id),
        "range"
      )
    );
  }
  if (filter.immatriculation) {
    operators.push(
      getOperator(
        "lower(offer_proposal_asset",
        "matriculation)",
        "EQUAL",
        globalConfig.formatters
          .getEqualValue(filter.immatriculation)
          .toLowerCase(),
        "immatriculation"
      )
    );
  }
  if (filter.commercialName) {
    operators.push(
      getOperator(
        "lower(party_party",
        "commercial_name)",
        "LIKE",
        globalConfig.formatters
          .getEqualValue(filter.commercialName + "%")
          .toLowerCase(),
        "commercialName"
      )
    );
  }
  if (filter.siren) {
    operators.push(
      getOperator(
        "lower(party_party",
        "siren)",
        "LIKE",
        globalConfig.formatters.getEqualValue(filter.siren + "%").toLowerCase(),
        "siren"
      )
    );
  }
  if (filter.role && filter.role.length > 0) {
    operators.push(
      getInOperator(
        "party_party",
        "id",
        "IN",
        null,
        null,
        globalConfig.formatters.getInValue(filter.role)
      )
    );
  } else {
    operators.push(
      getOperator(
        "offer_associated_party",
        "role_code",
        "EQUAL",
        "'CUSTOMER'",
        "role_code"
      )
    );
  }

  if (operators.length > 0) {
    qc_header.qc.otherFilters = {
      expressions: [
        {
          and: {
            operators,
          },
        },
      ],
    };
  }
  return qc_header.qc;
}

export function getQcParty(filter: any, offset?: any, limit?: any) {
  const qc_header: any = {
    qc: {
      queryId: "party-filter-search",
      offset: offset || 0,
      limit: limit || 100,
    },
  };

  qc_header.qc.parameters = {
    ...filter,
  };
  const operators = [];
  if (filter._reference) {
    operators.push(
      getOperator(
        "lower(party_party",
        "_reference)",
        "LIKE",
        globalConfig.formatters.getLikeValue(filter._reference).toLowerCase(),
        "_reference"
      )
    );
  }
  if (filter.reference) {
    operators.push(
      getOperator(
        "lower(party_party",
        "_reference)",
        "LIKE",
        globalConfig.formatters.getLikeValue(filter.reference).toLowerCase(),
        "_reference"
      )
    );
  }
  if (filter.siren) {
    operators.push(
      getOperator(
        "lower(party_party",
        "siren)",
        "LIKE",
        globalConfig.formatters.getLikeValue(filter.siren).toLowerCase(),
        "siren"
      )
    );
  }
  if (filter.family_name) {
    operators.push(
      getOperator(
        "lower(party_party",
        "family_name)",
        "LIKE",
          `'${filter.family_name.toLowerCase()}'`,
        "family_name"
      )
    );
  }

  if (filter.birthday) {
    operators.push(
      getOperator(
        "party_party",
        "birth_date",
        "EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.birthday,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "birthday"
      )
    );
  }
  if (filter.firstName) {
    operators.push(
      getOperator(
        "lower(party_party",
        "first_name)",
        "LIKE",
        `'${filter.firstName.toLowerCase()}'`,
        "firstName"
      )
    );
  }
  if (filter.discriminator) {
    operators.push(
      getOperator(
        "party_party",
        "_discriminator",
        "LIKE",
        globalConfig.formatters.getLikeValue(
          filter.discriminator === "party-FrenchPerson"
            ? "Person"
            : "Organization"
        ),
        "discriminator"
      )
    );
  }
  if (filter.city) {
    operators.push(
      getOperator(
        "party_postal_code",
        "city",
        "LIKE",
        globalConfig.formatters.getLikeValue(filter.city),
        "city"
      )
    );
  }
  if (filter.postalCode) {
    operators.push(
      getOperator(
        "party_postal_code",
        "postal_code",
        "LIKE",
        globalConfig.formatters.getLikeValue(filter.postalCode),
        "postalCode"
      )
    );
  }
  if (filter.siret) {
    operators.push(
      getOperator(
        "lower(party_facility",
        "siret)",
        "LIKE",
        globalConfig.formatters.getLikeValue(filter.siret).toLowerCase(),
        "siret"
      )
    );
  }
  if (filter.phoneNumber) {
    operators.push(
      getOperator(
        "party_contact_mechanism",
        "phone_number",
        "LIKE",
        globalConfig.formatters.getLikeValue(filter.phoneNumber),
        "phoneNumber"
      )
    );
  }
  if (filter.relationTypeCode) {
    operators.push(
      getOperator(
        "party_party_relation",
        "relation_type_code",
        "LIKE",
        globalConfig.formatters.getLikeValue(filter.relationTypeCode),
        "relationTypeCode"
      )
    );
  }
  if (filter.role) {
    operators.push(
      getOperator(
        "party_party_role",
        "role_code",
        "LIKE",
        globalConfig.formatters.getLikeValue(filter.role.value),
        "role_code"
      )
    );
  }
  if (filter.commercial_name) {
    operators.push(
      getOperator(
        "lower(party_party",
        "commercial_name)",
        "LIKE",
        globalConfig.formatters.getLikeValue(
          filter.commercial_name.toLowerCase()
        ),
        "commercial_name"
      )
    );
  }
  if (filter.current_status_code) {
    operators.push(
      getOperator(
        "party_party_role",
        "status_code",
        "LIKE",
        globalConfig.formatters.getLikeValue(filter.current_status_code.id),
        "status_code"
      )
    );
  }
  if (filter.externalReference) {
    operators.push(
      getOperator(
        "party_party",
        "external_reference",
        "LIKE",
        globalConfig.formatters.getLikeValue(filter.externalReference),
        "external_reference"
      )
    );
  }
   // Ne pas afficher les tiers supprimés
      operators.push(
      getOperator(
        "party_party",
        "is_deleted",
        "EQUAL",
        globalConfig.formatters.getEqualValue("false"),
        "is_deleted"
      )
    );

  if (operators.length > 0) {
    qc_header.qc.otherFilters = {
      expressions: [
        {
          and: {
            operators,
          },
        },
      ],
    };
  }
  return qc_header.qc;
}

export async function deleteFilter(filter: any) {
  const { resourceUid, systemUid } = filter;
  const url = `/${systemUid}/api/1/${systemUid}/filterstore/${resourceUid}/`;
  return (await api().delete(url));
}

export function isFilterDAAQMatching(filterDaaq: string, connectedUserDaaq: { readDAAQ: string, defaultCreateDAAQ: string }){
  const { readDAAQ, defaultCreateDAAQ } = connectedUserDaaq;
  if (readDAAQ === "/" || defaultCreateDAAQ === "/") return true; // In case user super admin or filter public.
  const segments = filterDaaq.split("/").filter((segment: string) => segment);
  const patterns = segments.map((_: any, index: number) => {
    const subPath = segments.slice(0, index + 1).join("/");
    return new RegExp(`^/${subPath}/$`);
  });
  return patterns.some((pattern: RegExp) => pattern.test(readDAAQ) || pattern.test(defaultCreateDAAQ));
}
export function getQcDeliverySchedule(filter: any, offset?: any, limit?: any) {
  const qc_header: any = {
    qc: {
      queryId: "deliverySchedule-search-filter-backup",
      offset: offset || 0,
      limit: limit || 100,
    },
  };

  qc_header.qc.parameters = {
    ...filter,
  };
  const operators = [];

  let clientNameFilter: any = null
  let localStorageReadDAAQ : any = undefined
  const authModule = localStorage.getItem(globalConfig.storageKeys.authModule)
  if (authModule) {
    localStorageReadDAAQ = JSON.parse(authModule)?.user && JSON.parse(authModule)?.user?.daaqList && JSON.parse(authModule)?.user?.daaqList.length  && JSON.parse(authModule)?.user?.daaqList[0]?.config?.readDAAQ
    ? JSON.parse(authModule)?.user?.daaqList[0]?.config?.readDAAQ
    : undefined
 }
  const parts = localStorageReadDAAQ?.split('/').filter((part:any) => part !== '');
  if (parts?.length === 3) {
    let localStorageUserId : any = undefined
    if (authModule) {
      localStorageUserId = JSON.parse(authModule)?.user?.resourceUid
    }
    operators.push(getOperator("order_assigned_user", "user_resourceuid", "EQUAL", globalConfig.formatters.getEqualValue(localStorageUserId)))
  } else if (localStorageReadDAAQ){
    operators.push(getOperator("user_daaq", "read_daaq", "LIKE", globalConfig.formatters.getEqualValue(localStorageReadDAAQ+'%' || store.state.authModule?.user?.daaqList[0]?.config?.readDAAQ+'%'), ""))
    if (filter && filter.filterActor && filter.filterActor.length > 0) {
      const listUsers = Array.isArray(filter.filterActor) ? filter.filterActor : [filter.filterActor];
      operators.push(getInOperator("order_assigned_user", "user_resourceuid", "IN", null, null, globalConfig.formatters.getInValue(listUsers)))
    }
  }
  if (filter && filter.role && filter.role.length>0){
    const roles = Array.isArray(filter.role) ? filter.role : [filter.role];
    operators.push(getInOperator("party_party_role", "role_code", "IN", null, null, globalConfig.formatters.getInValue(roles)))
  }
  else {
  operators.push(getInOperator("party_party_role", "role_code", "IN", null, null, globalConfig.formatters.getInValue(['VENDOR','SECRETARY','PREPARATEUR'])))
  }
  if (filter && filter.filterReference) {
      operators.push(getOperator( "lower(order_order_item","external_reference)","LIKE",globalConfig.formatters.getEqualValue(filter.filterReference + "%").toLowerCase(),"external_reference"));
  }

  if (filter && filter?.activity !== undefined) {
    operators.push(getOperator("order_order_asset","flag_new","EQUAL",globalConfig.formatters.getEqualValue(filter?.activity),"flag_new"));
  }
  if (filter && filter.filterStatus) {
      const filterStatusList = Array.isArray(filter.filterStatus) ? filter.filterStatus : [filter.filterStatus];
      if (filterStatusList.length > 0) {
        operators.push(getInOperator("order_order","status_code","IN",null,null,globalConfig.formatters.getInValue(filterStatusList)));
      }
  }
  if (filter && filter.filterGroup) {
      operators.push(getOperator("user_security_group", "id", "EQUAL", globalConfig.formatters.getEqualValue(filter.filterGroup), "id"))
              }
  if (filter?.startDate) {
    operators.push(
      getOperator(
        "cast(order_order_planning",
        "planning_date  AS date)",
        "GREATER_THAN_EQUAL",
        globalConfig.formatters.getEqualValue(moment(filter.startDate,  'DD/MM/YYYY').format('YYYY-MM-DD')),
        "planning_date"
      )
    );
} 
if (filter?.endDate) {
  operators.push(
    getOperator(
      "cast(order_order_planning",
      "planning_date  AS date)",
      "LESS_THAN_EQUAL",
      globalConfig.formatters.getEqualValue(moment(filter.endDate,  'DD/MM/YYYY').format('YYYY-MM-DD')),
      "planning_date"
    )
  );
} 
  if (filter && filter.filterBrand) {
    const brandValue = filter.filterBrand.includes('.') 
      ? filter.filterBrand.split('.').pop() 
      : filter.filterBrand;
      operators.push(
          getOperator(
            "lower(order_order_asset",
            "brand)",
            "LIKE",
            globalConfig.formatters.getEqualValue("%" + brandValue + "%").toLowerCase(),
            "brand"
          )
        );            
  }
  if (filter && filter.filterModel) {
    const modelValue = filter.filterModel.includes('.') 
      ? filter.filterModel.split('.').pop() 
      : filter.filterModel;
      operators.push(
          getOperator(
            "lower(order_order_asset",
            "_range)",
            "LIKE",
            globalConfig.formatters.getEqualValue("%" + modelValue + "%").toLowerCase(),
            "range"
          )
        );  
  }

  if(filter && filter.filterClient){
      clientNameFilter = buildClientNameFilter(filter.filterClient, "party_party");
  }

  if (filter && filter.planningType) {
    operators.push(getOperator("order_order_planning_type","internal_code","EQUAL",globalConfig.formatters.getEqualValue(filter.planningType),"planningType"));
    if (filter && filter.category) {
      operators.push(getOperator("order_order_planning_type","id","EQUAL",globalConfig.formatters.getEqualValue(filter.category),"category"));
    }
  }

  operators.push(
    getOperator("order_order_asset", "asset_trade_in_id", "ISNULL")
  );

  operators.push(getOperator("order_order", "status_code", "NOT_EQUAL", "'CANCELLED'", "status_code"))


      operators.push(
          getOperator(
              "order_order_party",
              "role_code",
              "EQUAL",
              "'CUSTOMER'",
              "role_code"
          )
      );


  
  const selectedUserNetworkJson=localStorage.getItem(SELECTED_USER_NETWORK)
  if(selectedUserNetworkJson){
    operators.push(
      getOperator(
        "party_party_role_network",
        "network_node_id",
        "EQUAL",
        globalConfig.formatters.getEqualValue(selectedUserNetworkJson),
        "network_node_id"
      )
    );

  }
  // Group pushed last (see QueryParser.buildFilterClause).
  if ( clientNameFilter ){
          operators.push(clientNameFilter)
  }

  if (operators.length > 0) {
    qc_header.qc.otherFilters = {
      expressions: [
        {
          and: {
            operators,
          },
        },
      ],
    };
  }
  return qc_header.qc;
}



export function getQcAides(filter: any, offset?: any, limit?: any): any {
      const qc_header: any = {
        qc: {
          queryId: "product-feature-search",
          offset: offset || 0,
          limit: limit || 100,
          parameters: {
            ...filter,
          },
        },
      };

      const operators:any = [];

  operators.push(
    getOperator(
      "product_product_feature",
      "_discriminator",
      "LIKE",
      globalConfig.formatters.getEqualValue('product-SubsidyProduct'),
      "_discriminator"
    )

        );
        
  if (filter && filter.name && filter.name !== "") {
            operators.push(
                getOperator(
                    "lower(product_product_feature",
                     "_name)",
                     "LIKE",
                      globalConfig.formatters.getEqualValue('%' +filter.name + '%').toLowerCase(),
                     ""))
  }
  
  if (filter?.from && filter?.from !== "") {
    operators.push(
      operator({
        val: filter.from,
        table: "product_product_feature",
        dbField: "validity_from",
      }).GREATER_THAN_EQUAL
    );
  }

  if (filter?.validity?.until && filter?.validity?.until !== "") {
    operators.push(
      operator({
        val: filter.until,
        table: "product_product_feature",
        dbField: "validity_until",
      }).LESS_THAN_EQUAL
    );
  }

  if(filter?.aide === true){
    operators.push(getOperator("product_product_feature", "validity_from", "ISNOTNULL"));
  }     
    

      if (operators.length > 0) {
        qc_header.qc.otherFilters = {
          expressions: [
            {
              and: {
                operators,
              },
            },
          ],
        };
      }
      return qc_header.qc;
}